/**
 * Satika multi-step checkout on /pages/checkout
 * Contact → Delivery → Payment (Razorpay modal) → Confirmation
 * Stays on this page — Admin orders via satika-api (no Shopify /checkout redirect).
 */

import { formatMoney } from '@theme/money-formatting';
import { CartUpdateEvent } from '@theme/events';

const STORAGE_KEY = 'satika_checkout_v1';

/**
 * @typedef {Object} CheckoutState
 * @property {string} email
 * @property {string} phone
 * @property {string} first_name
 * @property {string} last_name
 * @property {string} address1
 * @property {string} address2
 * @property {string} city
 * @property {string} zip
 * @property {string} province
 * @property {string} country
 * @property {string} shipping_title
 * @property {string} shipping_price
 * @property {string} shipping_handle
 * @property {string} payment_method
 */

/** @returns {CheckoutState} */
function emptyState() {
  return {
    email: '',
    phone: '',
    first_name: '',
    last_name: '',
    address1: '',
    address2: '',
    city: '',
    zip: '',
    province: '',
    country: 'India',
    shipping_title: '',
    shipping_price: '0',
    shipping_handle: '',
    payment_method: 'razorpay',
    step: 'contact',
  };
}



function getLiquidCustomer() {
  try {
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('shopifyCustomerLoggedOut') === 'true') {
      return null;
    }
    const el = document.getElementById('satika-checkout-liquid-customer');
    if (el && el.textContent) {
      const data = JSON.parse(el.textContent);
      if (data && typeof data === 'object') {
        if (!data.phone && data.default_address && data.default_address.phone) {
          data.phone = data.default_address.phone;
        }
        return data;
      }
    }
  } catch (e) {}
  return null;
}

function setProvinceSelect(selectEl, provinceValue) {
  if (!selectEl || !provinceValue) return '';
  const pRaw = String(provinceValue).trim();
  const pNorm = pRaw.toLowerCase().replace(/^in-/, '').trim();
  for (let i = 0; i < selectEl.options.length; i++) {
    const opt = selectEl.options[i];
    const optVal = opt.value.toLowerCase().replace(/^in-/, '').trim();
    const optText = opt.text.toLowerCase().trim();
    if (
      (optVal && optVal === pNorm) ||
      (optText && optText === pNorm) ||
      (pNorm.length > 2 && (optText.includes(pNorm) || pNorm.includes(optText)))
    ) {
      selectEl.selectedIndex = i;
      return opt.value;
    }
  }
  return '';
}

function sanitizeCheckoutError(err) {
  const msg = (err && err.message) ? String(err.message) : String(err || '');
  const lower = msg.toLowerCase();
  if (lower.includes('graphql') || lower.includes('401') || lower.includes('unauthorized') || lower.includes('access token') || lower.includes('api key') || lower.includes('500') || lower.includes('internal server error')) {
    return 'Our secure ordering gateway is finalizing your details. If your payment went through, our WhatsApp concierge will confirm your order details shortly.';
  }
  return msg || 'Order processing failed. Please try again or contact WhatsApp support.';
}

async function fetchCustomerProfileAsync(phone) {
  if (!phone) return null;
  const cleanPhone = phone.trim();
  const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const endpoints = [];
  if (isLocal) {
    endpoints.push(`http://127.0.0.1/satika-api/profile.php?phone=${encodeURIComponent(cleanPhone)}`);
  }
  endpoints.push(
    `/apps/wishlist/profile?phone=${encodeURIComponent(cleanPhone)}`,
    `https://wishlist.satika.live/api/profile?phone=${encodeURIComponent(cleanPhone)}`
  );
  for (const url of endpoints) {
    try {
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (res.ok) {
        const data = await res.json();
        if (data && data.success && data.customer) {
          try {
            localStorage.setItem('satika_customer_profile', JSON.stringify(data.customer));
            if (data.customer.email) localStorage.setItem('satika_customer_email', data.customer.email);
            if (data.customer.display_name) localStorage.setItem('satika_customer_name', data.customer.display_name);
          } catch (e) {}
          return data.customer;
        }
      }
    } catch (e) {}
  }
  return null;
}

/**
 * Resolves the authenticated customer profile across all available sources:
 * Liquid customer session, localStorage WhatsApp OTP session, cached satika_customer_profile.
 */
function getEffectiveCustomerProfile() {
  const liquidCust = getLiquidCustomer();
  let phone = '';
  let email = '';
  let firstName = '';
  let lastName = '';
  let displayName = '';
  let addresses = [];
  let defaultAddress = null;

  // 1. Check Liquid customer
  if (liquidCust && typeof liquidCust === 'object') {
    if (liquidCust.phone) phone = String(liquidCust.phone).trim();
    if (liquidCust.email) email = String(liquidCust.email).trim();
    if (liquidCust.first_name) firstName = String(liquidCust.first_name).trim();
    if (liquidCust.last_name) lastName = String(liquidCust.last_name).trim();
    if (liquidCust.name) displayName = String(liquidCust.name).trim();
    if (Array.isArray(liquidCust.addresses) && liquidCust.addresses.length > 0) {
      addresses = [...liquidCust.addresses];
    }
    if (liquidCust.default_address) {
      defaultAddress = liquidCust.default_address;
    }
  }

  // 2. Check localStorage WhatsApp login / verified profile
  try {
    const p = (localStorage.getItem('satika_customer_phone') || '').trim();
    if (p) phone = p;
    const em = (localStorage.getItem('satika_customer_email') || '').trim();
    if (em) email = em;
    const nm = (localStorage.getItem('satika_customer_name') || '').trim();
    if (nm && !nm.startsWith('+')) {
      displayName = nm;
      const parts = nm.split(' ');
      if (!firstName && parts[0]) firstName = parts[0];
      if (!lastName && parts.length > 1) lastName = parts.slice(1).join(' ');
    }

    const profRaw = localStorage.getItem('satika_customer_profile');
    if (profRaw) {
      const prof = JSON.parse(profRaw);
      if (prof && typeof prof === 'object') {
        if (prof.phone && !phone) phone = String(prof.phone).trim();
        if (prof.email && (!email || !email.includes('@'))) email = String(prof.email).trim();
        if (prof.first_name && !firstName) firstName = String(prof.first_name).trim();
        if (prof.last_name && !lastName) lastName = String(prof.last_name).trim();
        if (prof.display_name && !displayName) displayName = String(prof.display_name).trim();

        if (Array.isArray(prof.addresses) && prof.addresses.length > 0) {
          const existingIds = new Set(addresses.map((a) => a.id || `${a.address1}_${a.zip}`));
          prof.addresses.forEach((a) => {
            const key = a.id || `${a.address1}_${a.zip}`;
            if (a && !existingIds.has(key)) {
              addresses.push(a);
              existingIds.add(key);
            }
          });
        }
        if (!defaultAddress) {
          if (prof.default_address) {
            defaultAddress = prof.default_address;
          } else if (Array.isArray(prof.addresses) && prof.addresses.length > 0) {
            defaultAddress = prof.addresses.find((a) => a.is_default) || prof.addresses[0];
          }
        }
      }
    }

    const storedAddrsRaw = localStorage.getItem('satika_customer_addresses');
    if (storedAddrsRaw) {
      const storedAddrs = JSON.parse(storedAddrsRaw);
      if (Array.isArray(storedAddrs)) {
        const existingIds = new Set(addresses.map((a) => a.id || `${a.address1}_${a.zip}`));
        storedAddrs.forEach((a) => {
          const key = a.id || `${a.address1}_${a.zip}`;
          if (a && !existingIds.has(key)) {
            addresses.push(a);
            existingIds.add(key);
          }
        });
      }
    }
  } catch (e) {}

  // 3. Check URL query parameters (e.g. ?email=...&phone=...&name=...)
  if (typeof window !== 'undefined' && window.location.search) {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const uPhone = (urlParams.get('phone') || '').trim();
      const uEmail = (urlParams.get('email') || '').trim();
      const uFirstName = (urlParams.get('first_name') || '').trim();
      const uLastName = (urlParams.get('last_name') || '').trim();
      const uName = (urlParams.get('name') || '').trim();

      if (uPhone) {
        phone = uPhone;
        try { localStorage.setItem('satika_customer_phone', uPhone); } catch (e) {}
      }
      if (uEmail && uEmail.includes('@')) {
        email = uEmail;
        try { localStorage.setItem('satika_customer_email', uEmail); } catch (e) {}
      }
      if (uFirstName && !firstName) firstName = uFirstName;
      if (uLastName && !lastName) lastName = uLastName;
      if (uName && !firstName && !lastName) {
        const parts = uName.split(' ');
        firstName = parts[0] || '';
        lastName = parts.slice(1).join(' ') || '';
      }
    } catch (e) {}
  }

  if (!defaultAddress && addresses.length > 0) {
    defaultAddress = addresses.find((a) => a.is_default) || addresses[0];
  }

  if (!displayName && (firstName || lastName)) {
    displayName = `${firstName} ${lastName}`.trim();
  }

  if (email && email.toLowerCase().endsWith('@satika.live')) {
    email = '';
  }

  const isLoggedIn = Boolean(phone || (email && email.includes('@')));

  return {
    isLoggedIn,
    phone,
    email,
    first_name: firstName,
    last_name: lastName,
    display_name: displayName,
    addresses,
    default_address: defaultAddress,
  };
}

function loadState() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    let state = raw ? { ...emptyState(), ...JSON.parse(raw) } : emptyState();

    const prof = getEffectiveCustomerProfile();

    if (prof.isLoggedIn) {
      if (prof.phone && !state.phone) {
        state.phone = prof.phone;
      }
      if (prof.email && !state.email) {
        state.email = prof.email;
      }
      if (prof.first_name && !state.first_name) {
        state.first_name = prof.first_name;
      }
      if (prof.last_name && !state.last_name) {
        state.last_name = prof.last_name;
      }
      const def =
        prof.default_address ||
        (Array.isArray(prof.addresses) && prof.addresses.length > 0
          ? prof.addresses.find((a) => a.is_default) || prof.addresses[0]
          : null);
      if (def && (!state.address1 || !state.city || !state.zip)) {
        const parts = (def.name || `${def.first_name || ''} ${def.last_name || ''}`).trim().split(' ');
        if (!state.first_name) state.first_name = def.first_name || parts[0] || '';
        if (!state.last_name) state.last_name = def.last_name || parts.slice(1).join(' ') || '.';
        state.address1 = def.address1 || '';
        state.address2 = def.address2 || '';
        state.city = def.city || '';
        state.zip = def.zip || '';
        state.province = def.province_code || def.province || '';
        state.country = def.country || 'India';
      }
    }

    // Always let URL query parameters populate or override empty state fields
    if (typeof window !== 'undefined' && window.location.search) {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const urlEmail = (urlParams.get('email') || '').trim();
        const urlPhone = (urlParams.get('phone') || '').trim();
        const urlFirstName = (urlParams.get('first_name') || '').trim();
        const urlLastName = (urlParams.get('last_name') || '').trim();
        const urlName = (urlParams.get('name') || '').trim();
        const urlAddress1 = (urlParams.get('address1') || '').trim();
        const urlAddress2 = (urlParams.get('address2') || '').trim();
        const urlCity = (urlParams.get('city') || '').trim();
        const urlZip = (urlParams.get('zip') || urlParams.get('pincode') || urlParams.get('postal_code') || '').trim();
        const urlProvince = (urlParams.get('province') || urlParams.get('state') || '').trim();
        const urlCountry = (urlParams.get('country') || '').trim();
        const urlStep = (urlParams.get('step') || '').trim();

        if (urlEmail) state.email = urlEmail;
        if (urlPhone) state.phone = urlPhone;
        if (urlFirstName) state.first_name = urlFirstName;
        if (urlLastName) state.last_name = urlLastName;
        if (urlName && !state.first_name && !state.last_name) {
          const parts = urlName.split(' ');
          state.first_name = parts[0] || '';
          state.last_name = parts.slice(1).join(' ') || '';
        }
        if (urlAddress1) state.address1 = urlAddress1;
        if (urlAddress2) state.address2 = urlAddress2;
        if (urlCity) state.city = urlCity;
        if (urlZip) state.zip = urlZip;
        if (urlProvince) state.province = urlProvince;
        if (urlCountry) state.country = urlCountry;
        if (urlStep && (urlStep === 'delivery' || urlStep === 'payment')) state.step = urlStep;
      } catch (e) {}
    }

    return state;
  } catch {
    return emptyState();
  }
}

/**
 * Synchronizes checkout contact and address details to customer profile cache in localStorage
 * so that /pages/profile and other parts of the site reflect checkout inputs instantly.
 */
function syncCustomerDataToLocalProfile(data) {
  if (!data || typeof data !== 'object') return;
  try {
    const phone = (data.phone || localStorage.getItem('satika_customer_phone') || '').trim();
    const email = (data.email || localStorage.getItem('satika_customer_email') || '').trim();
    const firstName = (data.first_name || '').trim();
    const lastName = (data.last_name === '.' ? '' : (data.last_name || '')).trim();
    const fullName = (firstName || lastName)
      ? `${firstName} ${lastName}`.trim()
      : (data.name || localStorage.getItem('satika_customer_name') || '').trim();

    if (phone) localStorage.setItem('satika_customer_phone', phone);
    if (email && email.includes('@') && !email.endsWith('@satika.live')) {
      localStorage.setItem('satika_customer_email', email);
    }
    if (fullName) localStorage.setItem('satika_customer_name', fullName);

    let profile = {};
    try {
      const existing = localStorage.getItem('satika_customer_profile');
      if (existing) profile = JSON.parse(existing) || {};
    } catch (e) {}

    if (phone) profile.phone = phone;
    if (email && email.includes('@') && !email.endsWith('@satika.live')) profile.email = email;
    if (firstName) profile.first_name = firstName;
    if (lastName) profile.last_name = lastName;
    if (fullName) profile.display_name = fullName;

    // Delivery address sync
    if (data.address1 && data.city && data.zip) {
      if (!Array.isArray(profile.addresses)) profile.addresses = [];
      const newAddr = {
        id: data.id || ('local-addr-' + Date.now()),
        name: fullName || 'Recipient',
        first_name: firstName,
        last_name: lastName,
        phone: phone,
        address1: data.address1,
        address2: data.address2 || '',
        city: data.city,
        province: data.province || '',
        zip: data.zip,
        country: data.country || 'India',
        address_type: 'Home',
        is_default: true,
      };

      profile.addresses.forEach((a) => { a.is_default = false; });
      const matchIdx = profile.addresses.findIndex(
        (a) => a.address1 && a.address1.toLowerCase() === newAddr.address1.toLowerCase() && a.zip === newAddr.zip
      );
      if (matchIdx !== -1) {
        profile.addresses[matchIdx] = Object.assign({}, profile.addresses[matchIdx], newAddr);
      } else {
        profile.addresses.unshift(newAddr);
      }
      profile.default_address = newAddr;
      localStorage.setItem('satika_customer_addresses', JSON.stringify(profile.addresses));
    }

    localStorage.setItem('satika_customer_profile', JSON.stringify(profile));
    window.dispatchEvent(new CustomEvent('satika:customer:updated', { detail: profile }));
  } catch (err) {
    console.warn('[Satika Sync Local Profile Error]', err);
  }
}

/** @param {CheckoutState} state */
function saveState(state) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  try {
    if (state.phone) localStorage.setItem('satika_customer_phone', state.phone.trim());
    if (state.email && state.email.includes('@') && !state.email.endsWith('@satika.live')) {
      localStorage.setItem('satika_customer_email', state.email.trim());
    }
    const fullName = `${state.first_name || ''} ${state.last_name === '.' ? '' : (state.last_name || '')}`.trim();
    if (fullName) {
      localStorage.setItem('satika_customer_name', fullName);
    }
    syncCustomerDataToLocalProfile(state);
  } catch (e) {}
}

const STEP_ORDER = ['contact', 'delivery', 'payment', 'confirmation'];
const STEP_TRANSITION_MS = 320;
/** @type {WeakMap<ParentNode, number>} */
const stepTransitionTokens = new WeakMap();

/**
 * @param {number} ms
 * @returns {Promise<void>}
 */
function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * @param {HTMLElement} el
 * @param {string} prop
 * @param {number} fallbackMs
 * @returns {Promise<void>}
 */
function waitForTransition(el, prop, fallbackMs) {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      el.removeEventListener('transitionend', onEnd);
      resolve();
    };
    /** @param {TransitionEvent} event */
    const onEnd = (event) => {
      if (event.target !== el) return;
      if (prop && event.propertyName !== prop) return;
      finish();
    };
    el.addEventListener('transitionend', onEnd);
    setTimeout(finish, fallbackMs);
  });
}

/**
 * @param {ParentNode} root
 * @param {string} step
 * @param {{ animate?: boolean }} [options]
 * @returns {Promise<void>}
 */
async function showStep(root, step, options) {
  const animate = !(options && options.animate === false);
  const reduceMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const useMotion = animate && !reduceMotion;

  const idx = STEP_ORDER.indexOf(step);
  const panels = Array.from(root.querySelectorAll('[data-checkout-panel]')).filter(
    (panel) => panel instanceof HTMLElement
  );
  /** @type {HTMLElement | null} */
  const currentPanel =
    panels.find((panel) => !panel.hidden && !panel.classList.contains('is-leaving')) || null;
  const nextPanel =
    panels.find((panel) => panel.getAttribute('data-checkout-panel') === step) || null;

  if (!nextPanel) return;

  const currentName = currentPanel ? currentPanel.getAttribute('data-checkout-panel') || '' : '';
  const currentIdx = STEP_ORDER.indexOf(currentName);
  const goingBack = currentIdx > -1 && idx > -1 && idx < currentIdx;
  const directionClass = goingBack ? 'is-back' : 'is-forward';

  const currentState = loadState();
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneDigits = (currentState.phone || '').replace(/[^0-9]/g, '');
  const hasValidContact = emailRegex.test(currentState.email || '') && phoneDigits.length >= 10;
  const hasValidDelivery = Boolean(
    currentState.first_name && currentState.address1 && currentState.city && currentState.zip
  );

  root.querySelectorAll('[data-step-tab]').forEach((tab) => {
    if (!(tab instanceof HTMLElement)) return;
    const name = tab.getAttribute('data-step-tab') || '';
    const tabIdx = STEP_ORDER.indexOf(name);
    tab.classList.toggle('is-active', name === step);
    tab.classList.toggle('is-done', tabIdx > -1 && tabIdx < idx);

    // Make tabs clickable if user has completed prerequisites
    const canClick =
      name === 'contact' ||
      (name === 'delivery' && hasValidContact) ||
      (name === 'payment' && hasValidContact && hasValidDelivery);
    tab.disabled = !canClick;
    tab.style.cursor = canClick ? 'pointer' : 'default';
  });

  const stepsNav = root.querySelector('.satika-checkout__steps');
  const summary = root.querySelector('.satika-checkout__panel--summary');
  if (stepsNav instanceof HTMLElement) {
    stepsNav.hidden = false;
  }
  if (summary instanceof HTMLElement) {
    const hideSummary = step === 'confirmation';
    summary.hidden = hideSummary;
  }

  try {
    const current = loadState();
    saveState({ ...current, step });
  } catch {
    // ignore
  }

  if (currentPanel === nextPanel) {
    panels.forEach((panel) => {
      panel.hidden = panel !== nextPanel;
      panel.classList.remove('is-leaving', 'is-entering', 'is-forward', 'is-back');
    });
    return;
  }

  // Guaranteed panel visibility: nextPanel is immediately unhidden, other panels hidden
  panels.forEach((panel) => {
    if (panel === nextPanel) {
      panel.hidden = false;
      panel.classList.remove('is-leaving');
      if (useMotion) {
        panel.classList.add('is-entering', directionClass);
        // Force reflow
        void panel.offsetWidth;
        panel.classList.remove('is-entering');
      }
    } else {
      panel.hidden = true;
      panel.classList.remove('is-leaving', 'is-entering', 'is-forward', 'is-back');
    }
  });

  if (useMotion) {
    setTimeout(() => {
      nextPanel.classList.remove('is-forward', 'is-back');
    }, STEP_TRANSITION_MS + 20);
  }

  // Clean scroll to top
  try {
    const brand = root.querySelector('.satika-checkout__brand') || root;
    brand.scrollIntoView({ behavior: 'smooth', block: 'start' });
  } catch (e) {}
}

/**
 * @param {HTMLButtonElement | null} button
 * @param {boolean} loading
 * @param {string} [loadingLabel]
 * @param {string} [idleLabel]
 */
function setSubmitLoading(button, loading, loadingLabel, idleLabel) {
  if (!(button instanceof HTMLButtonElement)) return;
  if (loading) {
    if (!button.dataset.idleLabel) {
      button.dataset.idleLabel = idleLabel || button.textContent || 'Continue';
    }
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');
    button.textContent = loadingLabel || 'Please wait…';
    return;
  }
  button.disabled = false;
  button.removeAttribute('aria-busy');
  button.classList.remove('is-loading');
  button.textContent = idleLabel || button.dataset.idleLabel || 'Continue';
}

/**
 * @param {HTMLFormElement} form
 * @returns {HTMLButtonElement | null}
 */
function getFormSubmitButton(form) {
  const btn = form.querySelector('button[type="submit"]');
  return btn instanceof HTMLButtonElement ? btn : null;
}

/**
 * @param {HTMLFormElement} form
 * @param {string} message
 */
function setError(form, message) {
  const el = form.querySelector('[data-form-error]');
  if (!(el instanceof HTMLElement)) return;
  if (!message) {
    el.hidden = true;
    el.textContent = '';
    return;
  }
  el.hidden = false;
  el.textContent = message;
}

/**
 * @param {CheckoutState} state
 */
async function persistCartAttributes(state) {
  const noteParts = [
    `Checkout contact: ${state.email} / ${state.phone}`,
    `Ship to: ${state.first_name} ${state.last_name}`,
    `${state.address1}${state.address2 ? ', ' + state.address2 : ''}`,
    `${state.city}, ${state.province} ${state.zip}, ${state.country}`,
    state.shipping_title ? `Shipping: ${state.shipping_title}` : '',
    `Payment preference: ${state.payment_method}`,
  ].filter(Boolean);

  const body = {
    note: noteParts.join('\n'),
    attributes: {
      checkout_email: state.email,
      checkout_phone: state.phone,
      shipping_first_name: state.first_name,
      shipping_last_name: state.last_name,
      shipping_address1: state.address1,
      shipping_address2: state.address2,
      shipping_city: state.city,
      shipping_zip: state.zip,
      shipping_province: state.province,
      shipping_country: state.country,
      shipping_method: state.shipping_title,
      payment_preference: state.payment_method,
    },
  };

  const response = await fetch(window.Theme?.routes?.cart_update_url || '/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error('Could not save checkout details');
  }
  return response.json();
}

/**
 * @param {string} src
 * @returns {Promise<void>}
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

/**
 * Free gift for the order when the cart total after discounts meets the theme threshold.
 * @param {HTMLElement} root
 * @param {number} cartTotalCents
 * @returns {{ title: string } | null}
 */
function freeGiftForCart(root, cartTotalCents) {
  const title = (root.dataset.freeGiftTitle || '').trim();
  const minCents = Number(root.dataset.freeGiftMinCents || 0);
  if (!title || minCents <= 0 || cartTotalCents < minCents) return null;
  return { title };
}

/**
 * Free saree cover for the order when the cart total after discounts meets the theme minimum.
 * The checkout API applies the upper cap.
 * @param {HTMLElement} root
 * @param {number} cartTotalCents
 * @returns {{ title: string } | null}
 */
function sareeCoverForCart(root, cartTotalCents) {
  const title = (root.dataset.sareeCoverTitle || '').trim();
  const minCents = Number(root.dataset.sareeCoverMinCents || 0);
  if (!title || minCents <= 0 || cartTotalCents < minCents) return null;
  return { title };
}

/**
 * Create a real Shopify Admin order via satika-api (draft → complete).
 * @param {HTMLElement} root
 * @param {CheckoutState} state
 * @param {string} [paymentId]
 */
async function createAdminOrder(root, state, paymentId) {
  let primaryApiUrl = (root.dataset.orderApi || '/apps/wishlist/process-order').trim();
  if (!primaryApiUrl || primaryApiUrl.includes('digitalvertex') || primaryApiUrl.includes('satika-api')) {
    primaryApiUrl = '/apps/wishlist/process-order';
  }
  let fallbackApiUrl = (root.dataset.orderApiFallback || 'https://wishlist.satika.live/api/process-order').trim();
  if (!fallbackApiUrl || fallbackApiUrl.includes('digitalvertex') || fallbackApiUrl.includes('satika-api')) {
    fallbackApiUrl = 'https://wishlist.satika.live/api/process-order';
  }
  const apiToken = (root.dataset.orderApiToken || 'satika_chk_7f3a9c2e1b84d06e5a91f0c8b2d47e63').trim();

  const cartRes = await fetch('/cart.js');
  if (!cartRes.ok) throw new Error('Could not read cart.');
  const cart = await cartRes.json();
  if (!cart.items || !cart.items.length) {
    throw new Error('Your cart is empty.');
  }

  const shippingCents = Math.round(Number(state.shipping_price || 0) * 100);
  const discountCodes = (cart.discount_codes || [])
    .map((d) => (d && d.code ? String(d.code) : ''))
    .filter(Boolean);

  const payload = {
    email: state.email,
    phone: state.phone,
    payment_method: 'razorpay',
    razorpay_payment_id: paymentId || '',
    idempotency_key: paymentId || `rzp-${cart.token || Date.now()}`,
    total_cents: Number(cart.total_price || 0) + shippingCents,
    shipping_cents: shippingCents,
    discount_codes: discountCodes,
    note: cart.note || '',
    attributes: cart.attributes || {},
    shipping_address: {
      first_name: state.first_name,
      last_name: state.last_name,
      address1: state.address1,
      address2: state.address2,
      city: state.city,
      zip: state.zip,
      province_code: state.province,
      country_code: state.country === 'India' ? 'IN' : state.country,
    },
    shipping_rate: {
      title: state.shipping_title || 'Standard delivery',
    },
    line_items: cart.items.map((item) => ({
      variant_id: item.variant_id,
      quantity: item.quantity,
      title: item.product_title || item.title,
    })),
    free_gift: freeGiftForCart(root, Number(cart.total_price || 0)),
    saree_cover: sareeCoverForCart(root, Number(cart.total_price || 0)),
  };

  const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const endpoints = [];
  if (isLocal) {
    endpoints.push('http://127.0.0.1/satika-api/process-order.php');
  }
  endpoints.push(primaryApiUrl);
  if (fallbackApiUrl && fallbackApiUrl !== primaryApiUrl) {
    endpoints.push(fallbackApiUrl);
  }

  let data = null;
  let lastError = null;

  for (const endpoint of endpoints) {
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Satika-Checkout-Token': apiToken,
        },
        body: JSON.stringify(payload),
      });

      const jsonResult = await res.json().catch(() => ({}));
      if (res.ok && jsonResult.success) {
        data = jsonResult;
        break;
      } else {
        lastError = new Error((jsonResult && jsonResult.message) || `Order processing failed (HTTP ${res.status}).`);
      }
    } catch (err) {
      console.warn(`Order endpoint ${endpoint} failed, trying next:`, err);
      lastError = err;
    }
  }

  if (!data) {
    throw lastError || new Error('Could not create order in Admin.');
  }
  return { ...data, cart };
}

/**
 * @param {HTMLElement} root
 * @param {CheckoutState} state
 * @param {string} [paymentId]
 */
async function finishOnPage(root, state, paymentId) {
  const order = await createAdminOrder(root, state, paymentId);
  const ref = order.order_name || paymentId || 'Order placed';

  const cart = order.cart || {};
  const cartItems = (cart && cart.items) || [];
  const cartTotal = Number((cart && cart.total_price) || 0);
  const shippingCents = Math.round(Number(state.shipping_price || 0) * 100);

  const orderSummary = {
    order_name: ref,
    order_id: order.order_id || '',
    financial_status: order.financial_status || 'PAID',
    payment_method: 'Online Payment (Razorpay)',
    customer_name: `${state.first_name || ''} ${state.last_name || ''}`.trim() || 'Customer',
    phone: state.phone || '',
    email: state.email || '',
    shipping_address: {
      name: `${state.first_name || ''} ${state.last_name || ''}`.trim(),
      address1: state.address1 || '',
      address2: state.address2 || '',
      city: state.city || '',
      province: state.province || '',
      zip: state.zip || '',
      phone: state.phone || '',
    },
    items: cartItems.map((item) => ({
      title: item.product_title || item.title,
      variant_title: item.variant_title || '',
      quantity: item.quantity,
      price: (item.final_line_price || item.line_price || 0) / 100,
      unit_price: (item.final_price || item.price || 0) / 100,
      image: item.image || (item.featured_image && item.featured_image.url) || '',
    })),
    subtotal: cartTotal / 100,
    shipping: Number(state.shipping_price || 0),
    total: (cartTotal + shippingCents) / 100,
    created_at: new Date().toISOString(),
  };

  try {
    sessionStorage.setItem('satika_confirmed_order_summary', JSON.stringify(orderSummary));
    localStorage.setItem('satika_last_order_summary', JSON.stringify(orderSummary));
    localStorage.setItem('satika_last_order', JSON.stringify({ name: ref, id: order.order_id, time: Date.now() }));
    if (state.phone) {
      localStorage.setItem('satika_customer_phone', state.phone.trim());
    }
    if (state.first_name || state.last_name) {
      localStorage.setItem('satika_customer_name', `${state.first_name || ''} ${state.last_name || ''}`.trim());
    }
    if (state.email) {
      localStorage.setItem('satika_customer_email', state.email.trim());
    }
    syncCustomerDataToLocalProfile({
      ...state,
      phone: state.phone,
      email: state.email,
      first_name: state.first_name,
      last_name: state.last_name,
      address1: state.address1,
      address2: state.address2,
      city: state.city,
      province: state.province,
      zip: state.zip,
      country: state.country,
    });
  } catch (e) {}

  try {
    await fetch(window.Theme?.routes?.cart_clear_url || '/cart/clear.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
    });
    try {
      document.dispatchEvent(new CustomEvent('cart:updated'));
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch (e) {}
  } catch {
    // continue
  }

  sessionStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem('satika_checkout_pending_confirm');

  // Immediately render confirmation page on current view
  renderConfirmationPage(ref, orderSummary);

  // Update browser URL history so it can be reloaded/shared
  try {
    const newUrl = '/pages/checkout?confirmed=1&order=' + encodeURIComponent(ref);
    window.history.replaceState({ confirmed: true, order: ref }, '', newUrl);
  } catch (e) {}

  return order;
}

/**
 * Open Razorpay Checkout.js modal on this page (no /checkout redirect).
 * @param {HTMLElement} root
 * @param {CheckoutState} state
 * @param {HTMLButtonElement | null} submitBtn
 * @param {HTMLFormElement} paymentForm
 */
async function openRazorpay(root, state, submitBtn, paymentForm) {
  const key = (root.dataset.razorpayKey || '').trim();
  if (!key) {
    setError(
      paymentForm,
      'Add your Razorpay Key ID in Theme settings → Satika checkout.'
    );
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.textContent = 'Place order';
    }
    return;
  }

  await loadScript('https://checkout.razorpay.com/v1/checkout.js');

  /** @type {any} */
  const RazorpayCtor = window.Razorpay;
  if (typeof RazorpayCtor !== 'function') {
    throw new Error('Razorpay unavailable');
  }

  let amount = Number(root.dataset.cartTotal || 0);
  const shippingCents = Math.round(Number(state.shipping_price || 0) * 100);
  try {
    const cartRes = await fetch('/cart.js');
    if (cartRes.ok) {
      const cart = await cartRes.json();
      amount = Number(cart.total_price || amount);
    }
  } catch {
    // use data attribute
  }
  amount += shippingCents;

  const currency = (root.dataset.currency || 'INR').toUpperCase();
  const shopName = root.dataset.shopName || 'Satika';

  const rzp = new RazorpayCtor({
    key,
    amount,
    currency,
    name: shopName,
    description: 'Order payment',
    prefill: {
      name: `${state.first_name} ${state.last_name}`.trim(),
      email: state.email,
      contact: state.phone,
    },
    notes: {
      shipping: state.shipping_title,
      address: [state.address1, state.city, state.zip].filter(Boolean).join(', '),
    },
    theme: { color: '#1a1a1a' },
    modal: {
      ondismiss() {
        if (submitBtn) {
          setSubmitLoading(submitBtn, false, undefined, 'Place order');
        }
      },
    },
    handler(response) {
      const paymentId =
        response && response.razorpay_payment_id ? String(response.razorpay_payment_id) : '';
      if (submitBtn) {
        setSubmitLoading(submitBtn, true, 'Confirming order…', 'Place order');
      }
      finishOnPage(root, state, paymentId).catch((err) => {
        if (paymentId) {
          renderPaymentIssuePage({
            root,
            state,
            paymentId,
            amount,
            errorMessage: sanitizeCheckoutError(err),
          });
        } else {
          setError(
            paymentForm,
            sanitizeCheckoutError(err)
          );
          if (submitBtn) {
            setSubmitLoading(submitBtn, false, undefined, 'Place order');
          }
        }
      });
    },
  });

  rzp.on('payment.failed', function (resp) {
    if (submitBtn) {
      setSubmitLoading(submitBtn, false, undefined, 'Place order');
    }
    const description =
      resp?.error?.description || 'Your payment was not completed by your bank or UPI.';
    renderPaymentFailedNotice(paymentForm, description, () => {
      openRazorpay(root, state, submitBtn, paymentForm);
    });
  });

  rzp.open();
}

function defaultRateHtml() {
  return rateOptionHtml(
    {
      name: 'Standard delivery',
      price: '0.00',
      source: 'standard',
      code: 'standard',
      zone: '',
      note: '',
    },
    true
  );
}

/**
 * @param {ParentNode} root
 * @returns {Array<{code:string,name:string,zone?:string,price?:string,minimum_subtotal?:string|null,description?:string}>}
 */
function readAdminModes(root) {
  const node = root.querySelector('[data-admin-delivery-modes]');
  if (!(node instanceof HTMLElement)) return [];
  try {
    const parsed = JSON.parse(node.textContent || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * @param {Array<{code?:string,name?:string,zone?:string,price?:string|number,minimum_subtotal?:string|null,description?:string}>} modes
 * @param {string} selectedValue
 */
function renderModesHtml(modes, selectedValue) {
  if (!modes.length) return defaultRateHtml();
  const hasMatch = modes.some((mode, index) => (mode.code || `mode-${index}`) === selectedValue);
  return modes
    .map((mode, index) => {
      const code = mode.code || `mode-${index}`;
      const price = mode.price == null ? '0' : String(mode.price);
      const checked = hasMatch ? code === selectedValue : index === 0;
      let note = '';
      if (mode.minimum_subtotal && Number(mode.minimum_subtotal) > 0) {
        note = `From ₹${Number(mode.minimum_subtotal).toLocaleString('en-IN')}`;
      } else if (mode.description) {
        note = mode.description;
      }
      return rateOptionHtml(
        {
          name: mode.name || 'Shipping',
          price,
          source: mode.zone || '',
          code,
          zone: mode.zone || '',
          note,
        },
        checked
      );
    })
    .join('');
}

/**
 * Ensure at least one rate is selected in the list.
 * @param {HTMLElement} list
 * @param {HTMLFormElement} form
 * @returns {HTMLInputElement | null}
 */
function ensureSelectedRate(list, form) {
  let selected = form.querySelector('input[name="shipping_rate"]:checked');
  if (selected instanceof HTMLInputElement) return selected;

  if (!list.querySelector('input[name="shipping_rate"]')) {
    list.innerHTML = defaultRateHtml();
  }

  const first = list.querySelector('input[name="shipping_rate"]');
  if (first instanceof HTMLInputElement) {
    first.checked = true;
    return first;
  }
  return null;
}

/**
 * @param {CheckoutState} state
 * @param {HTMLElement} list
 * @param {HTMLElement | null} hint
 * @param {ParentNode} root
 */
async function loadShippingRates(state, list, hint, root) {
  const previous = list.querySelector('input[name="shipping_rate"]:checked');
  const previousValue = previous instanceof HTMLInputElement ? previous.value : '';
  const adminModes = readAdminModes(root);

  if (hint) hint.textContent = 'Updating delivery options for your address…';

  const params = new URLSearchParams({
    'shipping_address[zip]': state.zip,
    'shipping_address[country]': 'IN',
    'shipping_address[province]': state.province,
    'shipping_address[city]': state.city,
    'shipping_address[address1]': state.address1,
  });

  try {
    const response = await fetch(`/cart/shipping_rates.json?${params.toString()}`, {
      headers: { Accept: 'application/json' },
    });
    const data = await response.json();
    const liveRates = data?.shipping_rates || [];

    /** @type {Array<{code:string,name:string,zone?:string,price:string,minimum_subtotal?:string|null,description?:string}>} */
    let modes = adminModes.map((mode, index) => ({
      code: mode.code || `mode-${index}`,
      name: mode.name || 'Shipping',
      zone: mode.zone || '',
      price: mode.price == null ? '0' : String(mode.price),
      minimum_subtotal: mode.minimum_subtotal || null,
      description: mode.description || '',
    }));

    // Merge live carrier rates: update matching names, append new ones
    for (const [index, rate] of liveRates.entries()) {
      const liveName = (rate.name || rate.code || '').trim();
      const liveCode = rate.code || rate.name || `live-${index}`;
      const livePrice = rate.price == null ? '0' : String(rate.price);
      const matchIndex = modes.findIndex(
        (mode) => mode.name.toLowerCase() === liveName.toLowerCase() || mode.code === liveCode
      );
      if (matchIndex >= 0) {
        modes[matchIndex] = {
          ...modes[matchIndex],
          price: livePrice,
          name: liveName || modes[matchIndex].name,
        };
      } else if (liveName) {
        modes.push({
          code: String(liveCode).toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          name: liveName,
          zone: rate.source || 'Carrier',
          price: livePrice,
          minimum_subtotal: null,
          description: '',
        });
      }
    }

    if (!modes.length) {
      modes = [{ code: 'standard', name: 'Standard delivery', zone: 'Domestic', price: '0', minimum_subtotal: null }];
    }

    if (hint) {
      hint.textContent = liveRates.length
        ? 'All available delivery modes for your address.'
        : 'All store delivery modes. Live carrier rates will appear when available for this PIN.';
    }

    list.innerHTML = renderModesHtml(modes, previousValue || modes[0].code);
    let selected = list.querySelector('input[name="shipping_rate"]:checked');
    if (!selected) {
      const first = list.querySelector('input[name="shipping_rate"]');
      if (first instanceof HTMLInputElement) {
        first.checked = true;
      }
    }
  } catch {
    if (hint) hint.textContent = 'Showing all store delivery modes.';
    list.innerHTML = renderModesHtml(
      adminModes.length
        ? adminModes.map((mode, index) => ({
            code: mode.code || `mode-${index}`,
            name: mode.name || 'Shipping',
            zone: mode.zone || '',
            price: mode.price == null ? '0' : String(mode.price),
            minimum_subtotal: mode.minimum_subtotal || null,
            description: mode.description || '',
          }))
        : [{ code: 'standard', name: 'Standard delivery', zone: 'Domestic', price: '0' }],
      previousValue
    );
    let selected = list.querySelector('input[name="shipping_rate"]:checked');
    if (!selected) {
      const first = list.querySelector('input[name="shipping_rate"]');
      if (first instanceof HTMLInputElement) {
        first.checked = true;
      }
    }
  }
}

/**
 * @param {{ name: string, price: string, source: string, code: string, zone?: string, note?: string }} rate
 * @param {boolean} checked
 */
function rateOptionHtml(rate, checked) {
  const priceLabel =
    Number(rate.price) === 0
      ? 'Free'
      : `₹${Number(rate.price).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  const zone = rate.zone
    ? `<span class="satika-checkout__rate-zone">${escapeHtml(rate.zone)}</span>`
    : '';
  const note = rate.note
    ? `<span class="satika-checkout__rate-note">${escapeHtml(rate.note)}</span>`
    : '';
  return `
    <label class="satika-checkout__rate">
      <span>
        <input type="radio" name="shipping_rate" value="${escapeAttr(rate.code)}" data-title="${escapeAttr(rate.name)}" data-price="${escapeAttr(String(rate.price))}" data-zone="${escapeAttr(rate.zone || '')}" ${checked ? 'checked' : ''}>
        <span class="satika-checkout__rate-text">
          <span class="satika-checkout__rate-name">${escapeHtml(rate.name)}</span>
          ${zone}
          ${note}
        </span>
      </span>
      <strong>${escapeHtml(priceLabel)}</strong>
    </label>
  `;
}

/** @param {string} value */
function escapeHtml(value) {
  if (!value) return '';
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

/** @param {string} value */
function escapeAttr(value) {
  return escapeHtml(value).replaceAll("'", '&#39;');
}

/**
 * @param {HTMLElement} root
 * @param {number} cents
 * @returns {string}
 */
function formatCheckoutMoney(root, cents) {
  const currency = (root.dataset.currency || 'INR').toUpperCase();
  const useCode = root.dataset.currencyCodeEnabled === 'true';
  const format = useCode
    ? root.dataset.moneyWithCurrencyFormat || '₹{{amount}} {{currency}}'
    : root.dataset.moneyFormat || '₹{{amount}}';
  return formatMoney(Math.max(0, Math.round(cents)), format, currency);
}

/**
 * Apply selected shipping rate fields onto checkout state.
 * @param {CheckoutState} state
 * @param {HTMLInputElement | null} selected
 * @returns {CheckoutState}
 */
function applyShippingSelection(state, selected) {
  if (!(selected instanceof HTMLInputElement)) return state;
  return {
    ...state,
    shipping_handle: selected.value,
    shipping_title: selected.dataset.title || selected.value,
    shipping_price: selected.dataset.price || '0',
  };
}

/**
 * @param {HTMLElement} root
 * @param {CheckoutState} state
 */
function updateSummaryShipping(root, state) {
  const row = root.querySelector('[data-summary-shipping-row]');
  const shippingEl = root.querySelector('[data-summary-shipping]');
  const totalCell = root.querySelector('[data-summary-total]');
  if (!(row instanceof HTMLElement) || !(shippingEl instanceof HTMLElement)) return;

  const merchandiseCents = Number(root.dataset.cartTotal || 0);
  const originalCents = Number(root.dataset.cartOriginal || merchandiseCents);
  const shippingCents = Math.round(Number(state.shipping_price || 0) * 100);
  const hasShipping = Boolean(state.shipping_title);

  if (!hasShipping) {
    row.hidden = true;
  } else {
    row.hidden = false;
    const price = Number(state.shipping_price || 0);
    shippingEl.textContent =
      price === 0 ? 'Free' : `₹${price.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  }

  const payableCents = merchandiseCents + (hasShipping ? shippingCents : 0);
  const compareCents = originalCents + (hasShipping ? shippingCents : 0);
  const savingsCents = Math.max(0, compareCents - payableCents);

  if (totalCell instanceof HTMLElement) {
    const cut =
      savingsCents > 0
        ? `<s class="satika-checkout__price-cut satika-checkout__price-cut--total">${formatCheckoutMoney(root, compareCents)}</s>`
        : '';
    totalCell.innerHTML = `${cut}<strong class="satika-checkout__final-price">${formatCheckoutMoney(root, payableCents)}</strong>`;
  }

  const savingsEl = root.querySelector('[data-summary-savings]');
  if (savingsEl instanceof HTMLElement) {
    if (savingsCents > 0) {
      const template = savingsEl.dataset.savingsTemplate || 'Total savings __AMOUNT__';
      savingsEl.textContent = template.replace('__AMOUNT__', formatCheckoutMoney(root, savingsCents));
      savingsEl.hidden = false;
    } else {
      savingsEl.hidden = true;
    }
  }
}

/**
 * Change one cart line's quantity (0 removes it) and swap in the re-rendered summary.
 * @param {HTMLElement} root
 * @param {string} lineKey
 * @param {number} quantity
 * @param {AbortSignal} signal
 * @returns {Promise<{ itemCount: number }>}
 */
async function changeCartLine(root, lineKey, quantity, signal) {
  const sectionId = root.dataset.sectionId || '';
  const response = await fetch(window.Theme?.routes?.cart_change_url || '/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      id: lineKey,
      quantity,
      sections: sectionId,
      sections_url: window.location.pathname,
    }),
    signal,
  });
  const data = await response.json().catch(() => null);
  if (!response.ok || !data || data.errors) {
    const message = data && (data.description || data.message || data.errors);
    throw new Error(typeof message === 'string' ? message : '');
  }

  const itemCount = Number(data.item_count || 0);
  document.dispatchEvent(
    new CartUpdateEvent(data, sectionId, {
      itemCount,
      source: 'satika-checkout',
      sections: data.sections,
    })
  );

  const html = data.sections && data.sections[sectionId];
  if (itemCount === 0 || typeof html !== 'string') {
    return { itemCount, data };
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const nextRoot = doc.querySelector('[data-satika-checkout]');
  const nextSummary = doc.querySelector('[data-checkout-summary]');
  const summary = root.querySelector('[data-checkout-summary]');
  if (!(nextRoot instanceof HTMLElement) || !(nextSummary instanceof HTMLElement) || !summary) {
    return { itemCount, data };
  }

  for (const key of [
    'cartCount',
    'cartTotal',
    'cartOriginal',
    'freeGiftTitle',
    'freeGiftMinCents',
    'sareeCoverTitle',
    'sareeCoverMinCents',
  ]) {
    const value = nextRoot.dataset[key];
    if (value == null) delete root.dataset[key];
    else root.dataset[key] = value;
  }

  summary.replaceWith(nextSummary);
  return { itemCount, data };
}

/**
 * Quantity / remove buttons in the summary. Delegated on root because the summary is replaced.
 * @param {HTMLElement} root
 * @param {() => CheckoutState} getState
 * @param {() => Promise<void>} onCartChanged - Refreshes cart-dependent checkout state (shipping rates).
 */
function initCartLineControls(root, getState, onCartChanged) {
  /** @type {AbortController | null} */
  let pending = null;

  root.addEventListener('click', async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('[data-cart-line-quantity]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    const line = button.closest('[data-line-key]');
    const summary = button.closest('[data-checkout-summary]');
    if (!(line instanceof HTMLElement) || !(summary instanceof HTMLElement)) return;

    const lineKey = line.dataset.lineKey || '';
    const action = button.dataset.cartLineAction || '';
    const quantity = Math.max(0, Number(button.dataset.cartLineQuantity || 0));
    if (!lineKey) return;

    const executeChange = async (targetQty) => {
      pending?.abort();
      const controller = new AbortController();
      pending = controller;
      summary.setAttribute('aria-busy', 'true');

      const errorEl = summary.querySelector('[data-cart-update-error]');
      if (errorEl instanceof HTMLElement) errorEl.hidden = true;

      // Optimistic styling for removal
      if (targetQty === 0 && line) {
        line.style.opacity = '0.35';
        line.style.pointerEvents = 'none';
      }

      try {
        const { itemCount, data } = await changeCartLine(root, lineKey, targetQty, controller.signal);
        if (controller.signal.aborted) return;
        if (itemCount === 0) {
          window.location.reload();
          return;
        }

        // If removed and not already replaced by server HTML, remove line element from DOM
        if (targetQty === 0 && line && line.parentNode) {
          line.remove();
        }

        // Update totals if returned
        if (data && data.total_price != null) {
          const formatted = formatCheckoutMoney(root, data.total_price);
          summary.querySelectorAll('[data-checkout-total]').forEach((el) => {
            el.textContent = formatted;
          });
        }

        updateSummaryShipping(root, getState());
        const nextSummary = root.querySelector('[data-checkout-summary]');
        const nextLine = nextSummary?.querySelector(`[data-line-key="${CSS.escape(lineKey)}"]`);
        const focusTarget =
          nextLine?.querySelector(`[data-cart-line-action="${CSS.escape(action)}"]:not(:disabled)`) ||
          nextLine?.querySelector('[data-cart-line-quantity]:not(:disabled)');
        onCartChanged().catch(() => {});
        if (focusTarget instanceof HTMLElement) {
          focusTarget.focus();
        } else if (nextSummary instanceof HTMLElement) {
          const heading = nextSummary.querySelector('.satika-checkout__panel-title');
          if (heading instanceof HTMLElement) {
            heading.tabIndex = -1;
            heading.focus();
          }
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        if (line) {
          line.style.opacity = '';
          line.style.pointerEvents = '';
        }
        const liveSummary = root.querySelector('[data-checkout-summary]');
        liveSummary?.removeAttribute('aria-busy');
        const liveError = liveSummary?.querySelector('[data-cart-update-error]');
        if (liveError instanceof HTMLElement) {
          liveError.textContent =
            (error instanceof Error && error.message) || liveError.dataset.errorMessage || '';
          liveError.hidden = false;
        }
      }
    };

    if (action === 'remove') {
      const pTitle =
        line.dataset.productTitle ||
        line.querySelector('.satika-checkout__item-title')?.textContent?.trim() ||
        'Selected Item';
      const pPrice =
        line.dataset.productPrice ||
        line.querySelector('.satika-checkout__item-price span')?.textContent?.trim() ||
        '';
      const pImg = line.dataset.productImage || line.querySelector('img')?.src || '';
      const pHandle = line.dataset.productHandle || '';
      const vId = line.dataset.variantId || '';

      if (typeof window !== 'undefined' && window.SatikaCartRemoveModal) {
        window.SatikaCartRemoveModal.open({
          title: pTitle,
          price: pPrice,
          image: pImg,
          handle: pHandle,
          variantId: vId,
          onMoveToWishlist: async () => {
            if (window.SatikaWishlist && pHandle) {
              window.SatikaWishlist.addItem({
                handle: pHandle,
                title: pTitle,
                image: pImg,
                price: pPrice,
                variantId: vId,
              });
            }
            try {
              sessionStorage.setItem('satika_checkout_toast', 'Moved to your Wishlist!');
            } catch (e) {}

            await executeChange(0);

            if (typeof window.showToast === 'function') {
              window.showToast('Moved to your Wishlist!', 'success');
            } else if (window.SatikaWishlist && typeof window.SatikaWishlist.showToast === 'function') {
              window.SatikaWishlist.showToast('Moved to your Wishlist!', 'success');
            }
          },
          onRemovePermanently: async () => {
            await executeChange(0);
          },
          onCancel: () => {},
        });
        return;
      }

      await executeChange(0);
      return;
    }

    await executeChange(quantity);
  });
}

/**
 * @param {HTMLElement} root
 */
function initCheckout(root) {
  try {
    const pendingToast = sessionStorage.getItem('satika_checkout_toast');
    if (pendingToast) {
      sessionStorage.removeItem('satika_checkout_toast');
      setTimeout(() => {
        if (typeof window.showToast === 'function') {
          window.showToast(pendingToast, 'success');
        } else if (window.SatikaWishlist && typeof window.SatikaWishlist.showToast === 'function') {
          window.SatikaWishlist.showToast(pendingToast, 'success');
        }
      }, 350);
    }
  } catch (e) {}

  let state = loadState();
  let custProfile = getEffectiveCustomerProfile();

  const contactForm = root.querySelector('[data-checkout-form="contact"]');
  const deliveryForm = root.querySelector('[data-checkout-form="delivery"]');
  const paymentForm = root.querySelector('[data-checkout-form="payment"]');
  const authBadge = root.querySelector('#satika-checkout-auth-badge');
  const authSwitchBtn = root.querySelector('#satika-checkout-auth-switch');

  // Safely resolve contact inputs
  const contactEmailInput =
    contactForm?.querySelector('input[name="email"]') || contactForm?.elements?.namedItem('email');
  const contactPhoneInput =
    contactForm?.querySelector('input[name="phone"]') || contactForm?.elements?.namedItem('phone');

  // Sync profile/URL contact details into state if not already in state
  const rawCustEmail = (custProfile.email || '').trim();
  const realCustEmail = rawCustEmail.toLowerCase().endsWith('@satika.live') ? '' : rawCustEmail;
  if (!state.email && realCustEmail) state.email = realCustEmail;
  if (state.email && state.email.toLowerCase().endsWith('@satika.live')) state.email = '';
  if (!state.phone && custProfile.phone) state.phone = custProfile.phone;

  // Immediately prefill contact input elements if state has values
  if (contactEmailInput && state.email) contactEmailInput.value = state.email;
  if (contactPhoneInput && state.phone) contactPhoneInput.value = state.phone;

  // Also prefill state from inputs if inputs had server/inline-script values
  if (!state.email && contactEmailInput?.value) {
    const v = contactEmailInput.value.trim();
    if (!v.toLowerCase().endsWith('@satika.live')) state.email = v;
  }
  if (!state.phone && contactPhoneInput?.value) state.phone = contactPhoneInput.value.trim();
  saveState(state);

  // Validate contact & delivery readiness
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneDigits = (state.phone || '').replace(/[^0-9]/g, '');
  const hasValidContact = emailRegex.test(state.email || '') && phoneDigits.length >= 10;
  const hasValidDelivery = Boolean(
    state.first_name && state.address1 && state.city && state.zip
  );

  const urlParams =
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const explicitStep = urlParams.get('step');

  let resumeStep = state.step || 'contact';
  if (explicitStep === 'delivery' || explicitStep === 'payment' || explicitStep === 'contact') {
    resumeStep = explicitStep;
  } else if (hasValidContact && hasValidDelivery) {
    // Both contact and delivery are fully valid & filled -> advance straight to payment!
    resumeStep = 'payment';
    state.step = 'payment';
    saveState(state);
  } else if (hasValidContact) {
    // Contact is valid -> advance straight to delivery
    resumeStep = 'delivery';
    state.step = 'delivery';
    saveState(state);
  } else {
    resumeStep = 'contact';
    state.step = 'contact';
    saveState(state);
  }

  showStep(root, resumeStep, { animate: false });

  // Add click listeners to step navigation tabs
  root.querySelectorAll('[data-step-tab]').forEach((tab) => {
    tab.addEventListener('click', async (e) => {
      e.preventDefault();
      const targetStep = tab.getAttribute('data-step-tab');
      if (!targetStep || tab.disabled || targetStep === 'confirmation') return;

      const currentState = loadState();
      const emRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const phDigits = (currentState.phone || '').replace(/[^0-9]/g, '');
      const validContact = emRegex.test(currentState.email || '') && phDigits.length >= 10;
      const validDelivery = Boolean(
        currentState.first_name && currentState.address1 && currentState.city && currentState.zip
      );

      if (targetStep === 'contact') {
        await showStep(root, 'contact');
      } else if (targetStep === 'delivery') {
        if (!validContact) {
          if (contactForm instanceof HTMLFormElement) {
            contactForm.dispatchEvent(new Event('submit', { cancelable: true }));
          }
          return;
        }
        await showStep(root, 'delivery');
      } else if (targetStep === 'payment') {
        if (!validContact) {
          await showStep(root, 'contact');
          return;
        }
        if (!validDelivery) {
          if (deliveryForm instanceof HTMLFormElement) {
            deliveryForm.dispatchEvent(new Event('submit', { cancelable: true }));
          }
          return;
        }
        await showStep(root, 'payment');
      }
    });
  });

  /** @type {() => Promise<void>} */
  let refreshShippingRates = async () => {};
  initCartLineControls(root, () => state, () => refreshShippingRates());

  const updateAuthBadge = () => {
    if (!authBadge) return;
    const prof = getEffectiveCustomerProfile();
    const effectivePhone = prof.phone || state.phone || '';
    const rawEmail = (prof.email || state.email || '').trim();
    const effectiveEmail = rawEmail.toLowerCase().endsWith('@satika.live') ? '' : rawEmail;
    if (prof.isLoggedIn || effectivePhone || (effectiveEmail && effectiveEmail.includes('@'))) {
      authBadge.hidden = false;
      const phoneEl = authBadge.querySelector('[data-auth-phone]');
      const emailEl = authBadge.querySelector('[data-auth-email]');
      const cleanPhone = effectivePhone.replace(/[^0-9]/g, '');
      const pDisplay = cleanPhone ? ('+91 ' + cleanPhone.slice(-10)) : (prof.display_name || 'Verified Customer');
      if (phoneEl) phoneEl.textContent = pDisplay;
      if (emailEl) {
        emailEl.textContent = effectiveEmail ? ` (${effectiveEmail})` : '';
      }
    } else {
      authBadge.hidden = true;
    }
  };

  if (authSwitchBtn) {
    authSwitchBtn.addEventListener('click', (e) => {
      e.preventDefault();
      sessionStorage.removeItem(STORAGE_KEY);
      try {
        sessionStorage.setItem('shopifyCustomerLoggedOut', 'true');
        localStorage.removeItem('satika_customer_phone');
        localStorage.removeItem('satika_customer_name');
        localStorage.removeItem('satika_customer_email');
        localStorage.removeItem('satika_customer_profile');
        localStorage.removeItem('satika_customer_addresses');
      } catch (err) {}
      if (typeof window.openPhoneLoginModal === 'function') {
        window.openPhoneLoginModal(null, { clearPhone: true });
      } else {
        window.location.reload();
      }
    });
  }

  // Helper to cleanly apply an address object to delivery form and checkout state
  const applyAddressToForm = (addr, { updateState = true, highlight = false } = {}) => {
    if (!addr || !(deliveryForm instanceof HTMLFormElement)) return;
    const addrName = addr.name || `${addr.first_name || ''} ${addr.last_name || ''}`.trim() || 'Customer';
    const parts = addrName.split(' ');
    const fn = addr.first_name || parts[0] || state.first_name || '';
    const ln = addr.last_name || (parts.length > 1 ? parts.slice(1).join(' ') : '') || state.last_name || '.';

    if (deliveryForm.first_name) deliveryForm.first_name.value = fn;
    if (deliveryForm.last_name) deliveryForm.last_name.value = ln;
    if (deliveryForm.address1) deliveryForm.address1.value = addr.address1 || '';
    if (deliveryForm.address2) deliveryForm.address2.value = addr.address2 || '';
    if (deliveryForm.city) deliveryForm.city.value = addr.city || '';
    if (deliveryForm.zip) deliveryForm.zip.value = addr.zip || '';
    const matchedProv = setProvinceSelect(deliveryForm.province, addr.province_code || addr.province);
    if (deliveryForm.country) deliveryForm.country.value = addr.country || 'India';

    if (updateState) {
      state = {
        ...state,
        first_name: fn,
        last_name: ln,
        address1: addr.address1 || '',
        address2: addr.address2 || '',
        city: addr.city || '',
        zip: addr.zip || '',
        province: matchedProv || addr.province_code || addr.province || '',
        country: addr.country || 'India',
      };
      saveState(state);
    }

    if (highlight) {
      ['first_name', 'last_name', 'address1', 'city', 'zip', 'province'].forEach((f) => {
        if (deliveryForm[f]) {
          deliveryForm[f].classList.add('satika-field--highlight');
          setTimeout(() => deliveryForm[f].classList.remove('satika-field--highlight'), 800);
        }
      });
    }

    if (typeof refreshShippingRates === 'function') {
      refreshShippingRates();
    }
  };

  // Saved address UI containers
  const savedAddrContainer = root.querySelector('#satika-checkout-saved-addresses');
  const savedAddrGrid = root.querySelector('#satika-checkout-address-grid');
  const newAddrBtn = root.querySelector('#satika-checkout-new-address-toggle');

  const renderSavedAddressesList = (addresses) => {
    if (!Array.isArray(addresses) || addresses.length === 0) return;
    if (!(savedAddrContainer instanceof HTMLElement) || !(savedAddrGrid instanceof HTMLElement)) return;

    savedAddrContainer.hidden = false;
    savedAddrGrid.innerHTML = '';

    // Determine initial selected address index
    let selectedIdx = addresses.findIndex(
      (a) => state.address1 && (a.address1 === state.address1 || (a.zip && a.zip === state.zip))
    );
    if (selectedIdx === -1) {
      selectedIdx = addresses.findIndex((a) => a.is_default);
      if (selectedIdx === -1) selectedIdx = 0;
    }

    addresses.forEach((addr, idx) => {
      const card = document.createElement('div');
      const isSelected = idx === selectedIdx;

      card.className = 'satika-checkout__address-card' + (isSelected ? ' is-selected' : '');
      card.setAttribute('role', 'button');
      card.setAttribute('tabindex', '0');

      const addrName = addr.name || `${addr.first_name || ''} ${addr.last_name || ''}`.trim() || 'Saved Address';
      const type = addr.address_type || 'Home';
      const line = [addr.address1, addr.address2, addr.city, addr.province, addr.zip].filter(Boolean).join(', ');

      card.innerHTML = `
        <div class="satika-checkout__address-card-header">
          <span class="satika-checkout__address-card-name">${escapeHtml(addrName)}</span>
          <div class="satika-checkout__address-badges">
            ${addr.is_default ? '<span class="satika-checkout__address-card-tag satika-checkout__address-card-tag--default">Default</span>' : ''}
            <span class="satika-checkout__address-card-tag">${escapeHtml(type)}</span>
          </div>
        </div>
        <div class="satika-checkout__address-card-text">${escapeHtml(line)}</div>
        ${addr.phone ? `<div class="satika-checkout__address-card-phone">📞 ${escapeHtml(addr.phone)}</div>` : ''}
        <div class="satika-checkout__address-card-actions">
          <button type="button" class="satika-checkout__deliver-btn" data-deliver-btn>
            ${isSelected ? '✓ Delivering here' : 'Deliver to this address'}
          </button>
        </div>
      `;

      const applyAddr = () => {
        savedAddrGrid.querySelectorAll('.satika-checkout__address-card').forEach((c) => {
          c.classList.remove('is-selected');
          const b = c.querySelector('[data-deliver-btn]');
          if (b) b.textContent = 'Deliver to this address';
        });

        card.classList.add('is-selected');
        const deliverBtn = card.querySelector('[data-deliver-btn]');
        if (deliverBtn) deliverBtn.textContent = '✓ Delivering here';

        applyAddressToForm(addr, { updateState: true, highlight: true });
      };

      card.addEventListener('click', () => applyAddr());
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          applyAddr();
        }
      });

      savedAddrGrid.appendChild(card);
    });

    // Synchronously ensure form inputs reflect the selected address
    if (addresses[selectedIdx]) {
      applyAddressToForm(addresses[selectedIdx], { updateState: true, highlight: false });
    }
  };

  if (contactForm instanceof HTMLFormElement) {
    const emailInput =
      contactForm.querySelector('input[name="email"]') || contactForm.elements.namedItem('email');
    const phoneInput =
      contactForm.querySelector('input[name="phone"]') || contactForm.elements.namedItem('phone');

    if (!state.email && custProfile.email) state.email = custProfile.email;
    if (!state.phone && custProfile.phone) state.phone = custProfile.phone;

    if (emailInput && state.email) emailInput.value = state.email;
    if (phoneInput && state.phone) phoneInput.value = state.phone;
    saveState(state);
    updateAuthBadge();

    contactForm.querySelectorAll('input').forEach((inp) => {
      inp.addEventListener('input', () => {
        inp.classList.remove('satika-field--error');
        setError(contactForm, '');
      });
    });

    contactForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      setError(contactForm, '');

      const currentEmailInput =
        contactForm.querySelector('input[name="email"]') || contactForm.elements.namedItem('email');
      const currentPhoneInput =
        contactForm.querySelector('input[name="phone"]') || contactForm.elements.namedItem('phone');

      currentEmailInput?.classList?.remove('satika-field--error');
      currentPhoneInput?.classList?.remove('satika-field--error');

      const submitBtn = getFormSubmitButton(contactForm);
      const email = String(currentEmailInput?.value || '').trim();
      const phone = String(currentPhoneInput?.value || '').trim();

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const phoneDigits = phone.replace(/[^0-9]/g, '');

      if (!email) {
        setError(contactForm, 'Please enter your email address to receive order confirmation & tracking.');
        currentEmailInput?.classList?.add('satika-field--error');
        currentEmailInput?.focus();
        return;
      }
      if (!emailRegex.test(email)) {
        setError(contactForm, 'Please enter a valid email address (e.g. name@example.com).');
        currentEmailInput?.classList?.add('satika-field--error');
        currentEmailInput?.focus();
        return;
      }
      if (!phone) {
        setError(contactForm, 'Please enter your mobile phone number for delivery updates.');
        currentPhoneInput?.classList?.add('satika-field--error');
        currentPhoneInput?.focus();
        return;
      }
      if (phoneDigits.length < 10) {
        setError(contactForm, 'Please enter a valid 10-digit mobile number.');
        currentPhoneInput?.classList?.add('satika-field--error');
        currentPhoneInput?.focus();
        return;
      }

      setSubmitLoading(submitBtn, true, 'Continuing…', 'Continue');

      try {
        state = {
          ...state,
          email,
          phone,
          step: 'delivery',
          shipping_title: state.shipping_title || 'Standard delivery',
          shipping_price: state.shipping_price || '0',
          shipping_handle: state.shipping_handle || 'standard',
        };
        saveState(state);
        updateAuthBadge();

        // Background lookup if phone is present
        if (phone) {
          fetchCustomerProfileAsync(phone).then((customer) => {
            if (!customer) return;
            if (Array.isArray(customer.addresses) && customer.addresses.length > 0) {
              renderSavedAddressesList(customer.addresses);
            }
          });
        }

        await showStep(root, 'delivery');
        const deliveryList = root.querySelector('[data-shipping-list]');
        const deliveryStepForm = root.querySelector('[data-checkout-form="delivery"]');
        if (deliveryList instanceof HTMLElement && deliveryStepForm instanceof HTMLFormElement) {
          const selected = ensureSelectedRate(deliveryList, deliveryStepForm);
          state = applyShippingSelection(state, selected instanceof HTMLInputElement ? selected : null);
          saveState(state);
        }
        updateSummaryShipping(root, state);
      } catch (err) {
        console.error('Error continuing to delivery:', err);
        setError(contactForm, 'Could not continue. Please try again.');
      } finally {
        setSubmitLoading(submitBtn, false);
      }
    });
  }

  if (deliveryForm instanceof HTMLFormElement) {
    const fields = ['first_name', 'last_name', 'address1', 'address2', 'city', 'zip', 'province', 'country'];
    for (const name of fields) {
      const input = deliveryForm.querySelector(`[name="${name}"]`) || deliveryForm.elements.namedItem(name);
      if (input && 'value' in input) {
        if (state[name]) {
          if (name === 'province') {
            setProvinceSelect(input, state[name]);
          } else {
            input.value = state[name];
          }
        } else if (input.value) {
          state[name] = input.value;
        }
      }
    }

    // Initial pre-fill from cached customer profile
    if (custProfile.addresses && custProfile.addresses.length > 0) {
      renderSavedAddressesList(custProfile.addresses);
    } else if (custProfile.default_address) {
      applyAddressToForm(custProfile.default_address, { updateState: true, highlight: false });
    }

    // Background profile hydration for logged-in user to guarantee up-to-date information
    const phoneToLookup = custProfile.phone || state.phone || '';
    if (phoneToLookup) {
      fetchCustomerProfileAsync(phoneToLookup).then((customer) => {
        if (!customer) return;

        let stateChanged = false;

        // 1. Sync contact fields if empty or fallback
        if (customer.email && (!state.email || !state.email.includes('@') || state.email.endsWith('@satika.live'))) {
          state.email = customer.email;
          if (contactForm && contactForm.email) contactForm.email.value = customer.email;
          stateChanged = true;
        }
        if (customer.phone && !state.phone) {
          state.phone = customer.phone;
          if (contactForm && contactForm.phone) contactForm.phone.value = customer.phone;
          stateChanged = true;
        }

        // 2. Sync name fields if empty
        if (customer.first_name && (!deliveryForm.first_name.value || !state.first_name)) {
          deliveryForm.first_name.value = customer.first_name;
          state.first_name = customer.first_name;
          stateChanged = true;
        }
        if (customer.last_name && (!deliveryForm.last_name.value || !state.last_name)) {
          deliveryForm.last_name.value = customer.last_name;
          state.last_name = customer.last_name;
          stateChanged = true;
        }

        // 3. Sync saved addresses
        const addrs = customer.addresses || [];
        if (Array.isArray(addrs) && addrs.length > 0) {
          renderSavedAddressesList(addrs);
          const def = customer.default_address || addrs.find((a) => a.is_default) || addrs[0];
          if (def && (!deliveryForm.address1.value || !state.address1)) {
            applyAddressToForm(def, { updateState: true });
            stateChanged = true;
          }
        } else if (customer.default_address && (!deliveryForm.address1.value || !state.address1)) {
          applyAddressToForm(customer.default_address, { updateState: true });
          stateChanged = true;
        }

        updateAuthBadge();
        if (stateChanged) {
          saveState(state);
        }

        // Auto-advance if customer was on initial contact step and now has valid details
        const emailRgx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const pDigits = (state.phone || '').replace(/[^0-9]/g, '');
        const validCt = emailRgx.test(state.email || '') && pDigits.length >= 10;
        const validDel = Boolean(state.first_name && state.address1 && state.city && state.zip);

        const currentStepEl = root.querySelector('.satika-checkout__step.is-active');
        const activeTab = currentStepEl ? currentStepEl.getAttribute('data-step-tab') : 'contact';

        if (!explicitStep && activeTab === 'contact' && validCt) {
          if (validDel) {
            showStep(root, 'payment');
          } else {
            showStep(root, 'delivery');
          }
        }
      });
    }

    // Dynamic login / profile change listener
    const handleCustomerChange = () => {
      custProfile = getEffectiveCustomerProfile();
      state = loadState();
      if (contactForm && contactForm.email && state.email) contactForm.email.value = state.email;
      if (contactForm && contactForm.phone && state.phone) contactForm.phone.value = state.phone;
      updateAuthBadge();
      if (custProfile.addresses && custProfile.addresses.length > 0) {
        renderSavedAddressesList(custProfile.addresses);
      } else if (custProfile.default_address) {
        applyAddressToForm(custProfile.default_address, { updateState: true });
      }
      saveState(state);

      const emailRgx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const pDigits = (state.phone || '').replace(/[^0-9]/g, '');
      const validCt = emailRgx.test(state.email || '') && pDigits.length >= 10;
      const validDel = Boolean(state.first_name && state.address1 && state.city && state.zip);

      if (validCt && validDel) {
        showStep(root, 'payment');
      } else if (validCt) {
        showStep(root, 'delivery');
      }
    };

    document.addEventListener('satika:customer:login', handleCustomerChange);
    document.addEventListener('satika:customer:updated', handleCustomerChange);

    if (newAddrBtn) {
      newAddrBtn.addEventListener('click', (e) => {
        e.preventDefault();
        savedAddrGrid.querySelectorAll('.satika-checkout__address-card').forEach((c) => {
          c.classList.remove('is-selected');
          const b = c.querySelector('[data-deliver-btn]');
          if (b) b.textContent = 'Deliver to this address';
        });
        ['address1', 'address2', 'city', 'zip'].forEach((f) => {
          if (deliveryForm[f]) deliveryForm[f].value = '';
        });
        if (deliveryForm.province) deliveryForm.province.selectedIndex = 0;
        if (deliveryForm.address1) deliveryForm.address1.focus();
      });
    }

    const list = root.querySelector('[data-shipping-list]');
    const hint = root.querySelector('[data-shipping-hint]');

    if (list instanceof HTMLElement) {
      ensureSelectedRate(list, deliveryForm);
      if (!state.shipping_title) {
        state = applyShippingSelection(state, ensureSelectedRate(list, deliveryForm));
        saveState(state);
      }
      updateSummaryShipping(root, state);

      list.addEventListener('change', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        if (target.name !== 'shipping_rate') return;
        state = applyShippingSelection(state, target);
        saveState(state);
        updateSummaryShipping(root, state);
      });
    }

    const maybeFetchRates = async () => {
      const zip = String(deliveryForm.zip.value || '').trim();
      const province = String(deliveryForm.province.value || '').trim();
      const city = String(deliveryForm.city.value || '').trim();
      const address1 = String(deliveryForm.address1.value || '').trim();
      if (zip.length >= 5 && province && city && address1 && list instanceof HTMLElement) {
        state = {
          ...state,
          zip,
          province,
          city,
          address1,
          address2: String(deliveryForm.address2.value || '').trim(),
          first_name: String(deliveryForm.first_name.value || '').trim(),
          last_name: String(deliveryForm.last_name.value || '').trim(),
          country: String(deliveryForm.country.value || 'India'),
        };
        await loadShippingRates(state, list, hint instanceof HTMLElement ? hint : null, root);
        const selected = ensureSelectedRate(list, deliveryForm);
        state = applyShippingSelection(state, selected instanceof HTMLInputElement ? selected : null);
        saveState(state);
        updateSummaryShipping(root, state);
      }
    };
    refreshShippingRates = maybeFetchRates;

    ['zip', 'province', 'city', 'address1'].forEach((name) => {
      deliveryForm.elements.namedItem(name)?.addEventListener('change', () => {
        maybeFetchRates();
      });
    });

    // Smart Indian PIN code auto-lookup for City & State with zero-latency prefix lookup
    const zipInput = deliveryForm.querySelector('[name="zip"]') || deliveryForm.elements?.namedItem('zip');
    const pincodeStatusEl = root.querySelector('#satika-checkout-pincode-status');
    let lastLookupPin = '';

    const PIN_PREFIX_STATE = {
      '11': 'DL', '12': 'HR', '13': 'HR', '14': 'PB', '15': 'PB', '16': 'CH',
      '17': 'HP', '18': 'JK', '19': 'JK', '20': 'UP', '21': 'UP', '22': 'UP',
      '23': 'UP', '24': 'UP', '25': 'UP', '26': 'UP', '27': 'UP', '28': 'UP',
      '30': 'RJ', '31': 'RJ', '32': 'RJ', '33': 'RJ', '34': 'RJ', '36': 'GJ',
      '37': 'GJ', '38': 'GJ', '39': 'GJ', '40': 'MH', '41': 'MH', '42': 'MH',
      '43': 'MH', '44': 'MH', '45': 'MP', '46': 'MP', '47': 'MP', '48': 'MP',
      '49': 'CT', '50': 'TS', '51': 'AP', '52': 'AP', '53': 'AP', '56': 'KA',
      '57': 'KA', '58': 'KA', '59': 'KA', '60': 'TN', '61': 'TN', '62': 'TN',
      '63': 'TN', '64': 'TN', '67': 'KL', '68': 'KL', '69': 'KL', '70': 'WB',
      '71': 'WB', '72': 'WB', '73': 'WB', '74': 'WB', '75': 'OR', '76': 'OR',
      '77': 'OR', '78': 'AS', '80': 'BR', '81': 'BR', '82': 'JH', '83': 'JH',
      '84': 'BR', '85': 'BR'
    };

    const lookupPincode = async (rawPin) => {
      const clean = String(rawPin || '').replace(/\D/g, '').slice(0, 6);
      if (clean.length !== 6 || clean === lastLookupPin) return;
      lastLookupPin = clean;

      const cityEl = deliveryForm.querySelector('[name="city"]') || deliveryForm.elements?.namedItem('city');
      const provinceEl = deliveryForm.querySelector('[name="province"]') || deliveryForm.elements?.namedItem('province');

      // 1. Instant 0ms local state detection
      let fastState = '';
      if (clean.startsWith('403')) fastState = 'GA';
      else if (clean.startsWith('248') || clean.startsWith('249') || clean.startsWith('263')) fastState = 'UK';
      else if (clean.startsWith('825') || clean.startsWith('834') || clean.startsWith('831') || clean.startsWith('814')) fastState = 'JH';
      else fastState = PIN_PREFIX_STATE[clean.slice(0, 2)] || '';

      if (fastState && provinceEl) {
        setProvinceSelect(provinceEl, fastState);
        state.province = provinceEl.value;
      }

      if (pincodeStatusEl) {
        pincodeStatusEl.textContent = 'Verifying postal area…';
        pincodeStatusEl.style.display = 'block';
        pincodeStatusEl.style.color = '#6b7280';
      }

      // 2. Query official India Post public database for exact City / District
      try {
        const res = await fetch(`https://api.postalpincode.in/pincode/${clean}`);
        if (!res.ok) {
          if (pincodeStatusEl) pincodeStatusEl.style.display = 'none';
          return;
        }
        const data = await res.json();
        if (Array.isArray(data) && data[0]?.Status === 'Success' && Array.isArray(data[0]?.PostOffice) && data[0].PostOffice.length > 0) {
          const po = data[0].PostOffice[0];
          const autoCity = po.District || po.Division || po.Block || '';
          const autoState = po.State || '';

          if (cityEl && (!cityEl.value || cityEl.value.trim().length === 0 || cityEl.dataset.autofilled === 'true')) {
            cityEl.value = autoCity;
            cityEl.dataset.autofilled = 'true';
            state.city = autoCity;
            cityEl.dispatchEvent(new Event('input', { bubbles: true }));
          }

          if (provinceEl && autoState) {
            setProvinceSelect(provinceEl, autoState);
            state.province = provinceEl.value;
            provinceEl.dispatchEvent(new Event('change', { bubbles: true }));
          }

          if (pincodeStatusEl) {
            pincodeStatusEl.textContent = `✓ ${autoCity ? autoCity + ', ' : ''}${autoState || 'Verified'}`;
            pincodeStatusEl.style.color = '#059669';
            pincodeStatusEl.style.display = 'block';
          }

          saveState(state);
          maybeFetchRates();
        } else {
          if (pincodeStatusEl) pincodeStatusEl.style.display = 'none';
        }
      } catch (e) {
        if (pincodeStatusEl) pincodeStatusEl.style.display = 'none';
      }
    };

    if (zipInput instanceof HTMLInputElement) {
      zipInput.addEventListener('input', (e) => {
        const clean = (e.target.value || '').replace(/\D/g, '');
        if (clean.length === 6) {
          lookupPincode(clean);
        } else if (pincodeStatusEl) {
          pincodeStatusEl.style.display = 'none';
        }
      });
      zipInput.addEventListener('blur', (e) => {
        const clean = (e.target.value || '').replace(/\D/g, '');
        if (clean.length === 6) {
          lookupPincode(clean);
        }
      });
    }

    // Two-way sync: save fields immediately on blur so leaving or reloading preserves everything in Profile & Addresses
    ['first_name', 'last_name', 'address1', 'address2', 'city', 'zip', 'province'].forEach((fieldName) => {
      const el = deliveryForm.querySelector(`[name="${fieldName}"]`);
      if (el) {
        el.addEventListener('blur', () => {
          const val = el.value.trim();
          if (val) {
            state[fieldName] = val;
            saveState(state);
          }
        });
      }
    });

    deliveryForm.querySelectorAll('input, select').forEach((inp) => {
      inp.addEventListener('input', () => {
        inp.classList.remove('satika-field--error');
        setError(deliveryForm, '');
      });
      inp.addEventListener('change', () => {
        inp.classList.remove('satika-field--error');
        setError(deliveryForm, '');
      });
    });

    deliveryForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      setError(deliveryForm, '');

      ['first_name', 'last_name', 'address1', 'city', 'zip', 'province'].forEach((fieldName) => {
        const el = deliveryForm.querySelector(`[name="${fieldName}"]`);
        if (el) el.classList.remove('satika-field--error');
      });

      const submitBtn = getFormSubmitButton(deliveryForm);

      const getVal = (fieldName) => {
        const inp = deliveryForm.querySelector(`[name="${fieldName}"]`) || deliveryForm.elements?.namedItem(fieldName);
        return String(inp && 'value' in inp ? inp.value : '').trim();
      };

      const next = {
        ...state,
        first_name: getVal('first_name'),
        last_name: getVal('last_name'),
        address1: getVal('address1'),
        address2: getVal('address2'),
        city: getVal('city'),
        zip: getVal('zip'),
        province: getVal('province'),
        country: getVal('country') || 'India',
      };

      const missing = [];
      let firstInvalidEl = null;

      if (!next.first_name) {
        missing.push('First Name');
        const el = deliveryForm.querySelector('[name="first_name"]');
        if (el) { el.classList.add('satika-field--error'); if (!firstInvalidEl) firstInvalidEl = el; }
      }
      if (!next.last_name) {
        next.last_name = '.';
      }
      if (!next.address1) {
        missing.push('Street Address');
        const el = deliveryForm.querySelector('[name="address1"]');
        if (el) { el.classList.add('satika-field--error'); if (!firstInvalidEl) firstInvalidEl = el; }
      }
      if (!next.city) {
        missing.push('City');
        const el = deliveryForm.querySelector('[name="city"]');
        if (el) { el.classList.add('satika-field--error'); if (!firstInvalidEl) firstInvalidEl = el; }
      }
      if (!next.zip) {
        missing.push('PIN Code');
        const el = deliveryForm.querySelector('[name="zip"]');
        if (el) { el.classList.add('satika-field--error'); if (!firstInvalidEl) firstInvalidEl = el; }
      }
      if (!next.province) {
        missing.push('State');
        const el = deliveryForm.querySelector('[name="province"]');
        if (el) { el.classList.add('satika-field--error'); if (!firstInvalidEl) firstInvalidEl = el; }
      }

      if (missing.length > 0) {
        setError(deliveryForm, `Please fill in all required delivery fields: ${missing.join(', ')}.`);
        if (firstInvalidEl) firstInvalidEl.focus();
        return;
      }

      const cleanZip = next.zip.replace(/[^0-9]/g, '');
      if (cleanZip.length !== 6) {
        setError(deliveryForm, 'Please enter a valid 6-digit Indian PIN code.');
        const zipEl = deliveryForm.querySelector('[name="zip"]');
        if (zipEl) { zipEl.classList.add('satika-field--error'); zipEl.focus(); }
        return;
      }

      setSubmitLoading(submitBtn, true, 'Saving delivery…', 'Continue');

      try {
        if (list instanceof HTMLElement) {
          await loadShippingRates(next, list, hint instanceof HTMLElement ? hint : null, root);
        }

        const selected =
          list instanceof HTMLElement ? ensureSelectedRate(list, deliveryForm) : null;

        if (!(selected instanceof HTMLInputElement)) {
          setError(deliveryForm, 'Please choose a shipping method.');
          setSubmitLoading(submitBtn, false);
          return;
        }

        next.shipping_handle = selected.value;
        next.shipping_title = selected.dataset.title || selected.value;
        next.shipping_price = selected.dataset.price || '0';
        state = next;
        saveState(state);
        updateSummaryShipping(root, state);

        // Background save address & contact to Shopify so Profile is immediately in sync
        if (state.address1 && state.city && state.zip) {
          const bgPayload = {
            action: 'save_address',
            phone: state.phone || localStorage.getItem('satika_customer_phone') || '',
            email: state.email || localStorage.getItem('satika_customer_email') || '',
            first_name: state.first_name || '',
            last_name: state.last_name || '',
            address: {
              name: `${state.first_name || ''} ${state.last_name === '.' ? '' : (state.last_name || '')}`.trim(),
              first_name: state.first_name || '',
              last_name: state.last_name || '',
              phone: state.phone || '',
              address1: state.address1,
              address2: state.address2 || '',
              city: state.city,
              province: state.province || '',
              zip: state.zip,
              country: state.country || 'India',
              address_type: 'Home',
              is_default: true,
            }
          };
          if (bgPayload.phone) {
            fetch('/apps/wishlist/profile', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify(bgPayload)
            })
              .then((r) => r.json())
              .then((d) => {
                if (d && d.success && d.customer) {
                  localStorage.setItem('satika_customer_profile', JSON.stringify(d.customer));
                  if (Array.isArray(d.customer.addresses)) {
                    localStorage.setItem('satika_customer_addresses', JSON.stringify(d.customer.addresses));
                  }
                }
              })
              .catch(() => {
                fetch('https://wishlist.satika.live/api/profile', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                  body: JSON.stringify(bgPayload)
                }).catch(() => {});
              });
          }
        }

        try {
          await persistCartAttributes(state);
        } catch (e) {
          console.warn('Could not persist cart attributes, continuing:', e);
        }
        await showStep(root, 'payment');
      } catch (err) {
        console.error('Error saving delivery details:', err);
        setError(deliveryForm, 'Could not save delivery details. Please try again.');
      } finally {
        setSubmitLoading(submitBtn, false);
      }
    });
  }

  if (paymentForm instanceof HTMLFormElement) {
    paymentForm.addEventListener('change', (event) => {
      const target = event.target;
      if (target instanceof HTMLInputElement && target.name === 'payment_method') {
        const payOptions = paymentForm.querySelectorAll('.satika-checkout__pay-option');
        for (const opt of payOptions) {
          const radio = opt.querySelector('input[type="radio"]');
          if (radio instanceof HTMLInputElement && radio.checked) {
            opt.classList.add('satika-checkout__pay-option--selected');
          } else {
            opt.classList.remove('satika-checkout__pay-option--selected');
          }
        }
      }
    });

    paymentForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      setError(paymentForm, '');
      state = { ...state, payment_method: 'razorpay' };
      saveState(state);

      const submitBtn = paymentForm.querySelector('[data-pay-submit]') || getFormSubmitButton(paymentForm);
      setSubmitLoading(
        submitBtn instanceof HTMLButtonElement ? submitBtn : null,
        true,
        'Opening payment…',
        'Place order'
      );

      try {
        await persistCartAttributes(state);

        await openRazorpay(
          root,
          state,
          submitBtn instanceof HTMLButtonElement ? submitBtn : null,
          paymentForm
        );
      } catch (err) {
        setError(
          paymentForm,
          sanitizeCheckoutError(err)
        );
        setSubmitLoading(
          submitBtn instanceof HTMLButtonElement ? submitBtn : null,
          false,
          undefined,
          'Place order'
        );
      }
    });
  }

  root.querySelectorAll('[data-back]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      if (!(btn instanceof HTMLElement)) return;
      if (btn.getAttribute('aria-busy') === 'true') return;
      const step = btn.getAttribute('data-back');
      if (!step) return;
      btn.setAttribute('aria-busy', 'true');
      try {
        await showStep(root, step);
      } finally {
        btn.removeAttribute('aria-busy');
      }
    });
  });

  updateSummaryShipping(root, state);
}

/**
 * Renders the custom order successful page and full order summary
 * @param {string | null} [orderParam]
 * @param {any} [directSummary]
 */
async function renderConfirmationPage(orderParam, directSummary) {
  const container = document.getElementById('satika-checkout-confirmed-wrapper');
  if (!container) return;

  const emptyEl = document.getElementById('satika-checkout-empty');
  if (emptyEl) emptyEl.style.display = 'none';
  const activeFlow = document.getElementById('satika-checkout-active-flow');
  if (activeFlow) activeFlow.style.display = 'none';
  container.style.display = 'block';

  window.scrollTo({ top: 0, behavior: 'smooth' });

  let summary = directSummary || null;

  if (!summary) {
    try {
      const stored = sessionStorage.getItem('satika_confirmed_order_summary') || localStorage.getItem('satika_last_order_summary');
      if (stored) {
        summary = JSON.parse(stored);
      }
    } catch (e) {}
  }

  const orderName = (summary && summary.order_name) || orderParam || 'Your Order';

  // If storage didn't have full line item details, attempt to fetch from orders endpoint
  if (!summary || !summary.items || !summary.items.length) {
    const phone = (summary && summary.phone) || localStorage.getItem('satika_customer_phone') || '';
    if (phone) {
      try {
        container.innerHTML = `
          <div class="satika-confirm-card">
            <div class="satika-confirm-header">
              <div class="satika-confirm-icon-wrap"><div class="satika-confirm-icon">✓</div></div>
              <span class="satika-confirm-tag">Order Confirmed</span>
              <h1 class="satika-confirm-title">Order Placed Successfully!</h1>
              <p class="satika-confirm-lead">Loading order details for <strong class="satika-confirm-ref">${orderName}</strong>…</p>
            </div>
          </div>
        `;

        const res = await fetch(`/apps/wishlist/orders?phone=${encodeURIComponent(phone)}`);
        const data = await res.json().catch(() => ({}));
        if (data && data.success && Array.isArray(data.orders)) {
          const cleanRef = (orderName || '').replace(/[^0-9]/g, '');
          const matched = data.orders.find((o) => (o.name || '').replace(/[^0-9]/g, '') === cleanRef) || data.orders[0];
          if (matched) {
            summary = {
              order_name: matched.name,
              order_id: matched.id,
              financial_status: matched.financialStatus,
              payment_method: matched.financialStatus === 'PAID' ? 'Online Payment (Razorpay)' : 'Pending',
              customer_name: matched.recipientName || 'Customer',
              phone: matched.recipientPhone || phone,
              email: localStorage.getItem('satika_customer_email') || '',
              shipping_address: {
                name: matched.recipientName,
                address1: matched.shippingAddress || '',
                phone: matched.recipientPhone || phone,
              },
              items: (matched.lineItems || []).map((li) => ({
                title: li.title,
                variant_title: li.variantTitle || '',
                quantity: li.quantity,
                price: Number(li.unitPrice || 0) * Number(li.quantity || 1),
                unit_price: Number(li.unitPrice || 0),
                image: li.imageUrl || '',
              })),
              subtotal: Number(matched.totalPrice || 0),
              shipping: 0,
              total: Number(matched.totalPrice || 0),
              created_at: matched.createdAt,
            };
          }
        }
      } catch (err) {
        console.warn('Could not fetch past order details:', err);
      }
    }
  }

  const phone = (summary && summary.phone) || localStorage.getItem('satika_customer_phone') || '';
  const customerName = (summary && summary.customer_name) || localStorage.getItem('satika_customer_name') || 'Customer';
  const paymentMethod = (summary && summary.payment_method) || 'Online Payment (Razorpay)';
  const items = (summary && summary.items) || [];
  const subtotal = summary && summary.subtotal ? Number(summary.subtotal).toFixed(2) : '0.00';
  const shipping = summary && summary.shipping ? Number(summary.shipping).toFixed(2) : '0.00';
  const total = summary && summary.total ? Number(summary.total).toFixed(2) : subtotal;

  let itemsHtml = '';
  if (items.length > 0) {
    itemsHtml = items.map((it) => `
      <div class="satika-confirm-item">
        <div class="satika-confirm-item__img-wrap">
          ${it.image ? `<img src="${it.image}" alt="${(it.title || '').replace(/"/g, '&quot;')}" class="satika-confirm-item__img" loading="lazy">` : `<div class="satika-confirm-item__img-placeholder">👗</div>`}
        </div>
        <div class="satika-confirm-item__info">
          <div class="satika-confirm-item__title">${it.title || 'Pure Handloom Saree'}</div>
          ${it.variant_title ? `<div class="satika-confirm-item__variant">${it.variant_title}</div>` : ''}
          <div class="satika-confirm-item__qty">Qty: ${it.quantity} × ₹${Number(it.unit_price || 0).toFixed(2)}</div>
        </div>
        <div class="satika-confirm-item__price">₹${Number(it.price || 0).toFixed(2)}</div>
      </div>
    `).join('');
  } else {
    itemsHtml = '<div style="padding: 12px; color:#6b7280; font-size: 0.9rem;">Order items recorded. View live tracking in My Orders.</div>';
  }

  const addr = (summary && summary.shipping_address) || {};
  const addrParts = [addr.address1, addr.address2, addr.city, addr.province, addr.zip].filter(Boolean);
  const formattedAddress = addrParts.length > 0 ? addrParts.join(', ') : (addr.address1 || 'Standard delivery address');

  container.innerHTML = `
    <div class="satika-confirm-card">
      <div class="satika-confirm-header">
        <div class="satika-confirm-icon-wrap">
          <div class="satika-confirm-icon">✓</div>
        </div>
        <span class="satika-confirm-tag">Order Confirmed</span>
        <h1 class="satika-confirm-title">Order Placed Successfully!</h1>
        <p class="satika-confirm-lead">
          Thank you, <strong>${customerName}</strong>! Your order <strong class="satika-confirm-ref">${orderName}</strong> has been received and is being prepared with authentic handloom care.
        </p>

        <div class="satika-confirm-wa-notice">
          <span style="font-size: 1.25rem; flex-shrink: 0;">📱</span>
          <div>
            <strong>WhatsApp Updates Active:</strong> Confirmation and dispatch tracking will be shared on <strong>${phone ? phone : 'your mobile number'}</strong>.
          </div>
        </div>
      </div>

      <div class="satika-confirm-body">
        <div class="satika-confirm-col-items">
          <div class="satika-confirm-section-title">
            <span>Order Summary</span>
            <span style="font-size: 0.85rem; font-weight: 500; color: #6b7280;">${items.length} item(s)</span>
          </div>

          <div class="satika-confirm-items-list">
            ${itemsHtml}
          </div>

          <div class="satika-confirm-totals">
            <div class="satika-confirm-row">
              <span>Subtotal</span>
              <span>₹${subtotal}</span>
            </div>
            <div class="satika-confirm-row">
              <span>Delivery</span>
              <span>${Number(shipping) === 0 ? '<strong style="color:#059669;">FREE</strong>' : `₹${shipping}`}</span>
            </div>
            <div class="satika-confirm-row satika-confirm-row--total">
              <span>Total Amount</span>
              <span class="satika-confirm-total-amount">₹${total}</span>
            </div>
            <div class="satika-confirm-payment-badge">
              Payment Method: <strong>${paymentMethod}</strong>
            </div>
          </div>
        </div>

        <div class="satika-confirm-col-details">
          <div class="satika-confirm-info-box">
            <div class="satika-confirm-info-title">📦 Delivery Address</div>
            <div class="satika-confirm-info-text">
              <strong>${addr.name || customerName}</strong><br>
              ${formattedAddress}<br>
              Phone: ${phone || addr.phone || ''}
            </div>
          </div>

          <div class="satika-confirm-info-box satika-confirm-info-box--policy">
            <div class="satika-confirm-info-title">⏱ 12-Hour Cancellation Policy</div>
            <div class="satika-confirm-info-text">
              Standard handloom sarees can be managed or cancelled within <strong>12 hours</strong> directly from your account.<br>
              <span style="color:#9f1239; font-size:0.8rem; margin-top:4px; display:inline-block;">✨ Handcrafted custom pieces cannot be cancelled once placed.</span>
            </div>
          </div>
        </div>
      </div>

      <div class="satika-confirm-actions">
        <a href="/pages/orders" class="satika-confirm-btn satika-confirm-btn--primary">
          View My Orders & Tracking →
        </a>
        <a href="/collections/all" class="satika-confirm-btn satika-confirm-btn--secondary">
          ← Continue Shopping
        </a>
      </div>
    </div>
  `;
}

function savePaymentIssueLocally(issue) {
  try {
    const existingRaw = localStorage.getItem('satika_payment_issues');
    let issues = existingRaw ? JSON.parse(existingRaw) : [];
    if (!Array.isArray(issues)) issues = [];
    issues = issues.filter((i) => i && i.payment_id !== issue.payment_id);
    issues.unshift(issue);
    localStorage.setItem('satika_payment_issues', JSON.stringify(issues.slice(0, 10)));
  } catch (e) {}
}

function renderPaymentFailedNotice(form, description, onRetry) {
  const errEl = form.querySelector('[data-form-error]');
  if (!errEl) return;

  errEl.hidden = false;
  errEl.innerHTML = `
    <div class="satika-pay-failed-banner" style="background:#fff1f2; border:1px solid #fecdd3; border-radius:12px; padding:16px; margin:14px 0; text-align:left;">
      <div style="display:flex; gap:12px; align-items:flex-start;">
        <span style="font-size:24px; line-height:1;">⚠️</span>
        <div style="flex:1;">
          <strong style="color:#9f1239; font-size:0.95rem; display:block; margin-bottom:4px;">
            Payment Not Completed
          </strong>
          <p style="margin:0 0 6px 0; font-size:0.86rem; color:#be123c; line-height:1.45;">
            ${(description || 'Your payment was not completed by the issuing bank or UPI.').replace(/</g, '&lt;')}
          </p>
          <p style="margin:0 0 12px 0; font-size:0.82rem; color:#881337; line-height:1.45;">
            🛡️ <strong>No money was debited from your account.</strong> If any deduction happened at the bank level, it will be automatically reversed to your account within a few hours.
          </p>
          <div style="display:flex; flex-wrap:wrap; gap:10px;">
            <button type="button" class="satika-pay-retry-btn" style="background:#e11d48; color:#fff; border:none; padding:8px 16px; border-radius:8px; font-weight:600; font-size:0.85rem; cursor:pointer;">
              🔄 Retry Payment
            </button>
            <a href="https://wa.me/917848098586?text=${encodeURIComponent('Hi Satika, my payment was declined/incomplete at checkout. Could you help me complete my saree order?')}" target="_blank" rel="noopener noreferrer" style="display:inline-flex; align-items:center; gap:6px; background:#25D366; color:#fff; text-decoration:none; padding:8px 16px; border-radius:8px; font-weight:600; font-size:0.85rem;">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21 5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2zm5.79 14.02c-.24.68-1.19 1.25-1.95 1.41-.53.11-1.22.2-3.55-.76-2.98-1.23-4.9-4.24-5.05-4.44-.15-.2-1.21-1.61-1.21-3.07s.75-2.17 1.02-2.47c.27-.29.58-.36.77-.36.2 0 .39 0 .56.01.18.01.42-.07.65.5.24.58.82 2.01.89 2.15.07.15.12.32.02.51-.1.19-.15.31-.29.48-.15.17-.31.38-.44.51-.15.15-.3.31-.13.6.17.29.75 1.24 1.62 2.01 1.11.99 2.05 1.3 2.34 1.44.29.15.46.13.63-.08.17-.2.72-.84.91-1.13.19-.29.38-.24.64-.15.26.1 1.65.78 1.94.92.28.15.47.22.53.34.07.13.07.71-.17 1.39z"/></svg>
              Help on WhatsApp (+91 78480 98586)
            </a>
          </div>
        </div>
      </div>
    </div>
  `;

  const retryBtn = errEl.querySelector('.satika-pay-retry-btn');
  if (retryBtn && typeof onRetry === 'function') {
    retryBtn.addEventListener('click', () => {
      errEl.hidden = true;
      errEl.innerHTML = '';
      onRetry();
    });
  }
}

async function renderPaymentIssuePage({ root, state, paymentId, amount, errorMessage }) {
  const container = document.getElementById('satika-checkout-confirmed-wrapper');
  if (!container) return;

  const emptyEl = document.getElementById('satika-checkout-empty');
  if (emptyEl) emptyEl.style.display = 'none';
  const activeFlow = document.getElementById('satika-checkout-active-flow');
  if (activeFlow) activeFlow.style.display = 'none';
  container.style.display = 'block';

  window.scrollTo({ top: 0, behavior: 'smooth' });

  try {
    const newUrl = '/pages/checkout?issue=1&payment_id=' + encodeURIComponent(paymentId);
    window.history.replaceState({ issue: true, paymentId }, '', newUrl);
  } catch (e) {}

  const formattedAmount = (Number(amount || 0) / 100).toFixed(2);
  const customerName = `${state?.first_name || ''} ${state?.last_name || ''}`.trim() || 'Customer';
  const phone = (state?.phone || '').trim();

  savePaymentIssueLocally({
    payment_id: paymentId,
    amount: formattedAmount,
    customer_name: customerName,
    phone: phone,
    email: state?.email || '',
    date: new Date().toISOString(),
    status: 'pending_verification_or_refund',
    reason: errorMessage || 'Order sync delay',
  });

  try {
    await fetch(window.Theme?.routes?.cart_clear_url || '/cart/clear.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
    });
    document.dispatchEvent(new CustomEvent('cart:updated'));
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  } catch (e) {}

  sessionStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem('satika_checkout_pending_confirm');

  const waMessage = encodeURIComponent(
    `Hi Satika Support, I completed payment of ₹${formattedAmount} (Payment ID: ${paymentId}) on satika.live, but the order confirmation had a sync delay.\nName: ${customerName}\nPhone: ${phone}\nPlease confirm my order or refund status.`
  );
  const waUrl = `https://wa.me/917848098586?text=${waMessage}`;

  container.innerHTML = `
    <div class="satika-confirm-card" style="max-width: 680px; margin: 20px auto; background: #ffffff; border-radius: 16px; border: 1px solid #fed7aa; box-shadow: 0 10px 30px rgba(234, 88, 12, 0.08); overflow: hidden; padding: 32px 24px;">
      <div style="text-align: center; margin-bottom: 24px;">
        <div style="width: 64px; height: 64px; border-radius: 50%; background: #ffedd5; color: #ea580c; font-size: 32px; margin: 0 auto 16px; display: flex; align-items: center; justify-content: center;">
          🛡️
        </div>
        <span style="display: inline-block; background: #fff7ed; color: #c2410c; border: 1px solid #fdba74; padding: 4px 14px; border-radius: 999px; font-size: 0.78rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 10px;">
          Payment Received • Order Verification in Progress
        </span>
        <h1 style="font-size: 1.55rem; font-weight: 700; color: #1e293b; margin: 6px 0 10px; line-height: 1.3;">
          Your Payment is Safe &amp; Protected
        </h1>
        <p style="font-size: 0.95rem; color: #475569; max-width: 520px; margin: 0 auto; line-height: 1.55;">
          We recorded your payment of <strong>₹${formattedAmount}</strong> via Razorpay. Due to a momentary server sync delay, your order was not instantly confirmed, but our operations team has already received an alert.
        </p>
      </div>

      <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 18px; margin-bottom: 20px;">
        <div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 0.88rem;">
          <span style="color: #64748b;">Razorpay Payment ID:</span>
          <strong style="color: #0f172a; font-family: monospace; font-size: 0.94rem;">${paymentId}</strong>
        </div>
        <div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 0.88rem;">
          <span style="color: #64748b;">Amount Deducted:</span>
          <strong style="color: #0f172a; font-size: 0.95rem;">₹${formattedAmount}</strong>
        </div>
        <div style="display: flex; justify-content: space-between; font-size: 0.88rem;">
          <span style="color: #64748b;">Mobile Number:</span>
          <strong style="color: #0f172a;">${phone || 'N/A'}</strong>
        </div>
      </div>

      <div style="background: #f0fdf4; border: 1px solid #86efac; border-radius: 12px; padding: 18px; margin-bottom: 26px;">
        <div style="display: flex; gap: 14px; align-items: flex-start;">
          <div style="font-size: 26px; line-height: 1;">⏱️</div>
          <div>
            <strong style="color: #166534; font-size: 0.96rem; display: block; margin-bottom: 4px;">
              2 Working Days Auto-Refund Guarantee
            </strong>
            <p style="margin: 0; font-size: 0.86rem; color: #15803d; line-height: 1.55;">
              Our dispatch team is currently verifying this transaction. <strong>If your order cannot be manually created or fulfilled, the entire amount will be automatically refunded back to your original bank/UPI account within 2 working days.</strong>
            </p>
          </div>
        </div>
      </div>

      <div style="display: flex; flex-direction: column; gap: 12px;">
        <a href="${waUrl}" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center; justify-content: center; gap: 10px; background: #25D366; color: #ffffff; font-weight: 700; font-size: 0.98rem; text-decoration: none; padding: 14px 22px; border-radius: 10px; text-align: center; box-shadow: 0 4px 14px rgba(37, 211, 102, 0.25);">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21 5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2zm5.79 14.02c-.24.68-1.19 1.25-1.95 1.41-.53.11-1.22.2-3.55-.76-2.98-1.23-4.9-4.24-5.05-4.44-.15-.2-1.21-1.61-1.21-3.07s.75-2.17 1.02-2.47c.27-.29.58-.36.77-.36.2 0 .39 0 .56.01.18.01.42-.07.65.5.24.58.82 2.01.89 2.15.07.15.12.32.02.51-.1.19-.15.31-.29.48-.15.17-.31.38-.44.51-.15.15-.3.31-.13.6.17.29.75 1.24 1.62 2.01 1.11.99 2.05 1.3 2.34 1.44.29.15.46.13.63-.08.17-.2.72-.84.91-1.13.19-.29.38-.24.64-.15.26.1 1.65.78 1.94.92.28.15.47.22.53.34.07.13.07.71-.17 1.39z"/></svg>
          Chat on WhatsApp with Support (Instant Help)
        </a>
        <a href="/pages/orders" style="display: block; background: #1e293b; color: #ffffff; font-weight: 600; font-size: 0.95rem; text-decoration: none; padding: 13px 20px; border-radius: 10px; text-align: center;">
          View My Orders &amp; Track Status
        </a>
        <a href="/" style="display: block; background: transparent; color: #64748b; font-weight: 500; font-size: 0.88rem; text-decoration: none; padding: 8px 20px; text-align: center;">
          ← Back to Satika Home
        </a>
      </div>
    </div>
  `;
}

function renderPaymentIssueFromStorage(paymentId) {
  let issue = null;
  try {
    const raw = localStorage.getItem('satika_payment_issues');
    if (raw) {
      const list = JSON.parse(raw);
      if (Array.isArray(list)) {
        issue = list.find((i) => i && i.payment_id === paymentId) || list[0];
      }
    }
  } catch (e) {}

  if (issue) {
    renderPaymentIssuePage({
      paymentId: issue.payment_id || paymentId || 'Recorded',
      amount: Math.round(Number(issue.amount || 0) * 100),
      state: {
        first_name: issue.customer_name ? issue.customer_name.split(' ')[0] : 'Customer',
        last_name: issue.customer_name ? issue.customer_name.split(' ').slice(1).join(' ') : '',
        phone: issue.phone || '',
        email: issue.email || '',
      },
      errorMessage: issue.reason || 'Order sync delay',
    });
  }
}

async function startCheckoutApp() {
  const root = document.querySelector('[data-satika-checkout]');
  const params = new URLSearchParams(window.location.search);
  const isConfirmed = params.get('confirmed') === '1' || params.has('order');
  const orderParam = params.get('order');
  const isIssue = params.get('issue') === '1' || params.has('payment_issue');

  if (isConfirmed) {
    sessionStorage.removeItem('satika_checkout_pending_confirm');
    sessionStorage.removeItem(STORAGE_KEY);
    renderConfirmationPage(orderParam);
    return;
  }

  if (isIssue) {
    const paymentId = params.get('payment_id') || params.get('payment_issue') || '';
    renderPaymentIssueFromStorage(paymentId);
    return;
  }

  if (root instanceof HTMLElement) {
    let cartCount = Number(root.dataset.cartCount || 0);

    // If server rendered 0 items, check live /cart.js before giving up (e.g. after AJAX add-to-cart)
    if (cartCount === 0) {
      try {
        const cartRes = await fetch('/cart.js');
        if (cartRes.ok) {
          const cartData = await cartRes.json();
          if (cartData && cartData.item_count > 0) {
            window.location.reload();
            return;
          }
        }
      } catch (e) {}
    }

    if (cartCount > 0) {
      // Automatic discount code application from URL (e.g. ?discount=CODE or ?coupon=CODE)
      const urlDiscount = (params.get('discount') || params.get('coupon') || '').trim();
      if (urlDiscount) {
        try {
          const cartRes = await fetch('/cart.js');
          if (cartRes.ok) {
            const cartData = await cartRes.json();
            const appliedCodes = (cartData.discount_codes || []).map((d) => (d && d.code ? String(d.code).toLowerCase() : ''));
            if (!appliedCodes.includes(urlDiscount.toLowerCase())) {
              await fetch(window.Theme?.routes?.cart_update_url || '/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ discount: urlDiscount }),
              });
              document.dispatchEvent(new CustomEvent('cart:updated'));
            }
          }
        } catch (e) {}
      }

      initCheckout(root);
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startCheckoutApp);
} else {
  startCheckoutApp();
}
